import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { initializeApp } from "firebase/app";
import { getFirestore, doc, setDoc, getDoc, collection, query, orderBy, limit, getDocs, addDoc } from "firebase/firestore";
import { getAuth, signInAnonymously, onAuthStateChanged } from "firebase/auth";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell } from 'recharts';

const firebaseConfig = {
  apiKey: "AIzaSyDitsfnP_YVAsS7dU_klyKlTiQ5pVZdMwA",
  authDomain: "enac-661b9.firebaseapp.com",
  projectId: "enac-661b9",
  storageBucket: "enac-661b9.appspot.com",
  messagingSenderId: "56172799372",
  appId: "1:56172799372:web:96c7afdce1ebddb601e2a9",
  measurementId: "G-HC8JYK6SN4"
};

const LAST_LEVEL = 23;
const GAME_VERSION = "2.0";   // bumped: adds mode / presented-order / skip-state logging. Do not pool with <2.0 data.

// Fisher-Yates shuffle (returns a new array)
const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

// Presented play order. Level 1 (tutorial) is always first; only levels 2..LAST are reordered.
const buildLevelSequence = (mode) => {
    const rest = [];
    for (let l = 2; l <= LAST_LEVEL; l++) rest.push(l);
    return [1, ...(mode === 'shuffle' ? shuffle(rest) : rest)];
};

const newSessionId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Visual sizes for the new schematic (540x660 viewBox). MAX_INTERACTION_DISTANCE is kept at 44,
// unchanged from the old model, so the hit window and points formula in handleHit are identical;
// only the drawing size uses the radii below.
const DOT_RADIUS = 12;
const SEGMENT_RADIUS = 16;
const MAX_INTERACTION_DISTANCE = 44;

// Segment anchors placed on single-pass points of the tubule (PCT/DCT at their coil exits), so the
// dot passes each exactly once per lap — one clean, equal hit window per segment.
const segPos = {
  pct:  { x: 196, y: 166 },
  dlh:  { x: 208, y: 300 },
  thin: { x: 286, y: 570 },
  tal:  { x: 302, y: 352 },
  dct:  { x: 404, y: 188 },
  cd:   { x: 416, y: 474 },
};

const segOrder = ["pct", "dlh", "thin", "tal", "dct", "cd"];

// Textbook tubule: glomerulus exit -> PCT coil -> descending limb -> loop -> thick ascending -> DCT coil -> collecting duct
const NEPHRON_D = "M 82,120 C 92,114 104,128 108,120 C 150,96 182,120 168,150 C 158,172 126,168 140,146 C 150,130 186,138 196,166 C 214,206 200,250 210,300 C 218,344 212,372 220,414 C 226,480 234,556 268,574 C 302,592 320,552 304,508 C 290,452 302,398 302,344 C 302,300 324,258 336,214 C 346,188 328,156 352,150 C 374,145 380,174 356,178 C 340,181 346,158 368,154 C 394,166 414,192 416,246 C 420,326 416,432 416,522 L 416,612";

// Callout labels (leader endpoint + anchor). tal wraps onto two centred lines.
const SEG_LABELS = {
  pct:  { lines: ["Proximal convoluted tubule"], lx: 14,  ly: 184, anchor: "start"  },
  dlh:  { lines: ["Descending limb"],            lx: 16,  ly: 322, anchor: "start"  },
  thin: { lines: ["Thin ascending limb"],        lx: 150, ly: 626, anchor: "middle" },
  tal:  { lines: ["Thick ascending", "limb"],    lx: 360, ly: 398, anchor: "middle" },
  dct:  { lines: ["Distal convoluted tubule"],   lx: 436, ly: 120, anchor: "end"    },
  cd:   { lines: ["Collecting duct"],            lx: 446, ly: 500, anchor: "start"  },
};

const geneMap = {
  SLC12A1: "tal", KCNJ1: "tal", SLC12A3: "dct",
  WNK1: "dct", SCNN1A: "cd", NR3C2: "cd",
};

const START_POS = { x: 82, y: 120 };   // inside the glomerulus; dot respawns here each lap

const speedOptions = [
    { label: 'Novice', multiplier: 0.25 },
    { label: 'Easy', multiplier: 0.5 },
    { label: 'Normal', multiplier: 1 },
    { label: 'Hard', multiplier: 1.5 },
    { label: 'Expert', multiplier: 2.0 },
];

const levels = {
  1: { title: "Level 1", task: "Identify every segment", buttons: ["Identify"], map: { Identify: ["pct", "dlh", "thin", "tal", "dct", "cd"] }, facet: 'onboarding', feedback: { pct: "Proximal Convoluted Tubule: Reabsorbs ~65% of filtered water, Na+, and K+.", dlh: "Descending Loop of Henle: Highly permeable to water, reabsorbs water via osmosis.", thin: "Thin Ascending Limb: Impermeable to water, passive Na+ reabsorption.", tal: "Thick Ascending Limb (TAL): Actively reabsorbs Na+, K+, Cl-. Impermeable to water.", dct: "Distal Convoluted Tubule (DCT): Reabsorbs Na+, Cl-. Site of action for thiazide diuretics.", cd: "Collecting Duct: Regulates final water and electrolyte balance, site of ADH action."} },
  2: { title: "Level 2", task: "Identify all water-reabsorbing segments", buttons: ["Identify"], map: { Identify: ["pct", "dlh", "cd"] }, hideTargets: true, facet: 'anatomy', feedback: { pct: "Correct! The PCT is the site of the majority of water reabsorption (~65%).", dlh: "Correct! The descending limb is highly permeable to water.", cd: "Correct! The collecting duct is where final water reabsorption is regulated by ADH.", thin: "Incorrect. The thin ascending limb is impermeable to water.", tal: "Incorrect. The thick ascending limb is impermeable to water, creating a dilute filtrate.", dct: "Incorrect. The DCT is largely impermeable to water."} },
  3: {
    title: "Level 3", task: "Match diuretics to their site of action",
    buttons: ["Acetazolamide","Furosemide","Hydrochlorothiazide","Spironolactone","Dapagliflozin","Amiloride"],
    map: { Acetazolamide:"pct", Furosemide:"tal", Hydrochlorothiazide:"dct", Spironolactone:"cd", Dapagliflozin:"pct", Amiloride:"cd" },
    facet: 'diuretics',
    feedback: { Acetazolamide: "Correct! Acetazolamide inhibits carbonic anhydrase in the PCT.", Furosemide: "Correct! Furosemide inhibits the Na-K-2Cl cotransporter in the TAL.", Hydrochlorothiazide: "Correct! HCTZ inhibits the Na-Cl symporter in the DCT.", Spironolactone: "Correct! Spironolactone is an aldosterone antagonist in the collecting duct.", Dapagliflozin: "Correct! SGLT2 inhibitors like Dapagliflozin work in the PCT.", Amiloride: "Correct! Amiloride blocks the ENaC channel in the collecting duct."}
  },
  4: {
    title: "Level 4", task: "Match each syndrome to its correct (major) nephron location",
    buttons: ["Fanconi","Bartter","Gitelman","Nephrogenic DI"],
    map: { Fanconi:"pct", Bartter:"tal", Gitelman:"dct", "Nephrogenic DI":"cd" },
    facet: 'tubulopathies',
    feedback: { 
        Fanconi: "Correct! Fanconi syndrome is a generalized dysfunction of the PCT.", 
        Bartter: "Correct! Bartter syndrome (of all subtypes) involves defects in transporters in the TAL.", 
        Gitelman: "Correct! Gitelman syndrome is caused by a defect in the Na-Cl symporter in the DCT.", 
        "Nephrogenic DI": "Correct! NDI is characterized by resistance to ADH in the collecting duct.",
        "Bartter_dct": "Incorrect. Not all subtypes of Bartter syndrome involve the DCT, though it may be involved in types 3 and 4.",
        "Bartter_cd": "Incorrect. Not all subtypes of Bartter syndrome involve the CD, though it may be involved in type 2."
    }
  },
  5: {
    title: "Level 5", task: "For a patient with hyperkalemia, use each K+-excreting diuretic at its site of action",
    buttons: ["Acetazolamide","Furosemide","Hydrochlorothiazide", "Spironolactone", "Amiloride"],
    map: { Acetazolamide: "pct", Furosemide: "tal", Hydrochlorothiazide: "dct" },
    facet: 'diuretics',
    feedback: { Acetazolamide: "Correct. By increasing Na+ delivery downstream, acetazolamide promotes K+ excretion.", Furosemide: "Correct. Loop diuretics cause significant K+ wasting.", Hydrochlorothiazide: "Correct. Thiazides increase K+ excretion.", Spironolactone: "Incorrect. Spironolactone is a K+-sparing diuretic and would worsen hyperkalemia.", Amiloride: "Incorrect. Amiloride is a K+-sparing diuretic."}
  },
  6: { 
    title: "Level 6", task: "For a patient with SIADH, use the appropriate diuretic at its site of action", 
    buttons:["Furosemide", "Hydrochlorothiazide"], 
    map:{Furosemide:"tal"},
    facet: 'diuretics',
    feedback: { Furosemide: "Correct. Loop diuretics interfere with the kidney's ability to concentrate urine, making them useful in SIADH.", Hydrochlorothiazide: "Incorrect. Thiazides can worsen hyponatremia in SIADH."}
  },
  7: {
    title: "Level 7", task: "For a patient with hypokalemia, use each K+-sparing diuretic at its site of action",
    buttons:["Spironolactone","Amiloride", "Furosemide", "Hydrochlorothiazide", "Dapagliflozin"],
    map:{ Spironolactone:"cd", Amiloride:"cd" },
    facet: 'diuretics',
    feedback: { Spironolactone: "Correct. Aldosterone antagonists are K+-sparing.", Amiloride: "Correct. ENaC blockers are K+-sparing.", Furosemide: "Incorrect. Loop diuretics cause K+ wasting.", Hydrochlorothiazide: "Incorrect. Thiazides cause K+ wasting.", Dapagliflozin: "Incorrect. SGLT2 inhibitors are not primarily K+-sparing."}
  },
  8: {
    title: "Level 8", task: "For a patient with hypomagnesemia, use each Mg++-sparing diuretic at its site of action",
    buttons:["Spironolactone","Amiloride","Dapagliflozin", "Furosemide", "Hydrochlorothiazide"],
    map:{ Spironolactone:"cd", Amiloride:"cd", Dapagliflozin:"pct" },
    facet: 'diuretics',
    feedback: { Spironolactone: "Correct. K+-sparing diuretics can also be Mg++-sparing.", Amiloride: "Correct. K+-sparing diuretics can also be Mg++-sparing.", Dapagliflozin: "Correct. SGLT2 inhibitors have a modest Mg++-sparing effect.", Furosemide: "Incorrect. Loop diuretics cause significant Mg++ wasting.", Hydrochlorothiazide: "Incorrect. Thiazides cause Mg++ wasting."}
  },
  9: {
    title: "Level 9", task: "For a patient with hypercalciuria, use the Ca++-sparing diuretic at its site of action",
    buttons:["Furosemide","Hydrochlorothiazide"], map:{ Hydrochlorothiazide:"dct" },
    facet: 'diuretics',
    feedback: { Hydrochlorothiazide: "Correct. Thiazides increase Ca++ reabsorption in the DCT.", Furosemide: "Incorrect. Loop diuretics increase Ca++ excretion."}
  },
  10:{
    title: "Level 10", task: "For a patient with NDI, use the combo therapy at their respective sites of action",
    buttons:["Hydrochlorothiazide","Amiloride", "Furosemide"], map:{ Hydrochlorothiazide:"dct", Amiloride:"cd" },
    facet: 'diuretics',
    feedback: { Hydrochlorothiazide: "Correct. Thiazides reduce polyuria in NDI by causing mild volume depletion.", Amiloride: "Correct. Amiloride can be used to counteract the hypokalemia from thiazides.", Furosemide: "Incorrect. Loop diuretics would worsen polyuria."}
  },
  11:{
    title: "Level 11", task: "Match RTA types to their segment",
    buttons:["Type 1 RTA","Type 2 RTA","Type 4 RTA"],
    map:{ "Type 1 RTA":"cd", "Type 2 RTA":"pct", "Type 4 RTA":"cd" },
    facet: 'tubulopathies',
    feedback: { "Type 1 RTA": "Correct. Distal RTA (Type 1) is a defect in H+ secretion in the collecting duct.", "Type 2 RTA": "Correct. Proximal RTA (Type 2) is a defect in bicarbonate reabsorption in the PCT.", "Type 4 RTA": "Correct. Type 4 RTA is caused by aldosterone deficiency or resistance in the collecting duct."}
  },
  12:{
    title: "Level 12", task: "A baby has hyponatremia, hyperkalemia, ↑ renin & aldo. Click the correct segment",
    buttons:["Identify"],
    map:{ "Identify": "cd" },
    facet: 'tubulopathies',
    feedback: { cd: "Correct. This presentation suggests pseudohypoaldosteronism type 1, an ENaC channel defect in the collecting duct."}
  },
  13:{ 
    title: "Level 13", task: "A baby has failure to thrive, hypophosphatemia, hypokalemia, aminoaciduria, & glycosuria. Hit the affected segment.", 
    buttons:["Identify"], 
    map:{ "Identify":"pct" },
    facet: 'tubulopathies',
    feedback: { pct: "Correct. These are the classic signs of Fanconi syndrome, a generalized PCT dysfunction."}
  },
  14:{
    title: "Level 14", task: "A patient has polyuria and hypernatremia despite high ADH levels. Click the faulty receptor.",
    buttons:["Vasopressin receptor","ENaC","Mineralocorticoid receptor"], map:{ "Vasopressin receptor":"cd" },
    facet: 'tubulopathies',
    feedback: { "Vasopressin receptor": "Correct. Resistance to ADH (vasopressin) in the collecting duct causes Nephrogenic Diabetes Insipidus.", ENaC: "Incorrect. ENaC is involved in Na+ balance, but not the primary cause of ADH resistance.", "Mineralocorticoid receptor": "Incorrect. This receptor is for aldosterone, not ADH."}
  },
  15:{
    title: "Level 15", task: "A patient has hypokalemia, hypomagnesemia, and metabolic alkalosis with low urinary calcium. Click the defective transporter.",
    buttons:["Na-Cl symporter","Na K 2Cl transporter","ENaC"], map:{ "Na-Cl symporter":"dct" },
    facet: 'tubulopathies',
    feedback: { "Na-Cl symporter": "Correct. This is the classic presentation of Gitelman syndrome, a defect in the NCC transporter in the DCT.", "Na K 2Cl transporter": "Incorrect. A defect here would cause Bartter syndrome, which has high urinary calcium.", ENaC: "Incorrect. ENaC defects cause different syndromes (Liddle or PHA-1)."}
  },
  16:{
    title: "Level 16", task: "A patient has hypertension, hypokalemia, and metabolic alkalosis with low aldosterone. Click the overactive channel.",
    buttons:["ENaC","Mineralocorticoid receptor"], map:{ ENaC:"cd" },
    facet: 'tubulopathies',
    feedback: { ENaC: "Correct. A gain-of-function mutation in ENaC causes Liddle syndrome, which presents this way.", "Mineralocorticoid receptor": "Incorrect. While MR activation causes similar symptoms, low aldosterone points to a primary channel defect."}
  },
  17:{
    title: "Level 17", task: "A patient has hypertension & hypokalemia after eating too much black licorice. Click the activated receptor.",
    buttons:["Mineralocorticoid receptor","ENaC"], map:{ "Mineralocorticoid receptor":"cd" },
    facet: 'tubulopathies',
    feedback: { "Mineralocorticoid receptor": "Correct. Glycyrrhizic acid in licorice inhibits an enzyme that protects the MR from cortisol, leading to its activation.", ENaC: "Incorrect. ENaC is downstream of the MR, but the primary event is receptor activation."}
  },
  18:{ title: "Level 18", task: "Match gene mutations to their segment", buttons: Object.keys(geneMap), map: geneMap, facet: 'genetics',
    feedback: { SLC12A1: "Correct. SLC12A1 codes for the NKCC2 transporter in the TAL (defect causes Bartter syndrome).", KCNJ1: "Correct. KCNJ1 codes for the ROMK potassium channel in the TAL.", SLC12A3: "Correct. SLC12A3 codes for the NCC transporter in the DCT (defect causes Gitelman syndrome).", WNK1: "Correct. WNK kinases regulate NCC and are mutated in some inherited hypertension forms.", SCNN1A: "Correct. SCNN1A codes for a subunit of the ENaC channel in the collecting duct.", NR3C2: "Correct. NR3C2 codes for the Mineralocorticoid Receptor."}
  },
  19: {
    title: "Level 19",
    task: "Identify all segments that reabsorb sodium through ACTIVE transport",
    buttons: ["Identify"],
    map: { Identify: ["pct", "tal", "dct", "cd"] },
    hideTargets: true,
    facet: 'anatomy',
    feedback: {
      pct: "Correct! The PCT actively reabsorbs ~65% of filtered Na+ via Na+/K+-ATPase on the basolateral membrane.",
      tal: "Correct! The TAL actively reabsorbs Na+, K+, and Cl- via NKCC2; it is impermeable to water.",
      dct: "Correct! The DCT actively reabsorbs Na+ and Cl- via the NCC cotransporter.",
      cd: "Correct! The collecting duct actively reabsorbs Na+ via ENaC, driven by the basolateral Na+/K+-ATPase.",
      dlh: "Incorrect. The descending limb primarily reabsorbs water passively — it has minimal active Na+ transport.",
      thin: "Incorrect. The thin ascending limb allows passive Na+ diffusion out, but this is not active transport."
    }
  },
  20: {
    title: "Level 20",
    task: "Match each transport protein to its nephron segment",
    buttons: ["Aquaporin-1 (2 segments)", "NKCC2", "NCC", "Aquaporin-2"],
    map: { "Aquaporin-1 (2 segments)": ["pct", "dlh"], "NKCC2": "tal", "NCC": "dct", "Aquaporin-2": "cd" },
    facet: 'anatomy',
    feedback: {
      "Aquaporin-1 (2 segments)": "Correct! AQP1 is expressed in both the PCT and the descending limb of Henle, enabling constitutive water reabsorption in both segments.",
      pct: "Correct! AQP1 is constitutively expressed in the PCT, enabling high-volume water reabsorption.",
      dlh: "Correct! AQP1 is also present in the descending limb, allowing water to follow the osmotic gradient into the medulla.",
      "NKCC2": "Correct! NKCC2 (Na-K-2Cl cotransporter) is the primary transporter in the TAL and the target of loop diuretics.",
      "NCC": "Correct! NCC (Na-Cl cotransporter) is the principal transporter in the DCT and the target of thiazides.",
      "Aquaporin-2": "Correct! AQP2 in the collecting duct is regulated by ADH/vasopressin — it inserts into the apical membrane in response to ADH."
    }
  },
  21: {
    title: "Level 21",
    task: "A girl has hypomagnesemia and seizures from birth. Match the defective channel to its segment.",
    buttons: ["TRPM6", "NKCC2", "NCC"],
    map: { TRPM6: "dct" },
    facet: 'genetics',
    feedback: {
      TRPM6: "Correct! TRPM6 encodes the epithelial Mg2+ channel in the DCT. Loss-of-function mutations cause familial hypomagnesemia with secondary hypocalcemia (HSH), presenting with seizures in infancy.",
      NKCC2: "Incorrect. NKCC2 is the TAL transporter — its defects cause Bartter syndrome, not primary hypomagnesemia.",
      NCC: "Incorrect. NCC defects cause Gitelman syndrome, which can include hypomagnesemia but typically presents later and without neonatal seizures."
    }
  },
  22: {
    title: "Level 22",
    task: "A child with renal tubular acidosis also has hearing loss. Match the gene to its segment.",
    buttons: ["ATP6V1B1", "SLC12A3", "SCNN1A"],
    map: { ATP6V1B1: "cd" },
    facet: 'genetics',
    feedback: {
      ATP6V1B1: "Correct! ATP6V1B1 encodes the B1 subunit of the vacuolar H+-ATPase in the collecting duct. Mutations cause distal RTA with sensorineural hearing loss, as the same proton pump is expressed in the inner ear.",
      SLC12A3: "Incorrect. SLC12A3 (NCC) mutations cause Gitelman syndrome — a salt-wasting disorder without acidosis or deafness.",
      SCNN1A: "Incorrect. SCNN1A (ENaC) mutations cause Liddle syndrome or PHA-1 — neither presents with RTA or hearing loss."
    }
  },
  23: {
    title: "Level 23",
    task: "A child has polyuria, metabolic alkalosis, and sensorineural hearing loss. Match the gene to its segment.",
    buttons: ["BSND", "SLC12A1", "ATP6V1B1"],
    map: { BSND: "tal" },
    facet: 'genetics',
    feedback: {
      BSND: "Correct! BSND encodes Barttin, the essential beta-subunit of the ClC-Ka and ClC-Kb chloride channels in the TAL. Mutations cause Bartter syndrome type IV — the only Bartter subtype with sensorineural deafness, because Barttin is also expressed in the cochlea.",
      SLC12A1: "Incorrect. SLC12A1 (NKCC2) mutations cause Bartter type I — also TAL-based with alkalosis and polyuria, but without hearing loss.",
      ATP6V1B1: "Incorrect. ATP6V1B1 mutations cause distal RTA with deafness — but RTA presents with acidosis, not alkalosis."
    }
  },
};

// --- UI Components ---
const ShockwaveEffect = ({ x, y, color, onComplete }) => {
    useEffect(() => {
        const timer = setTimeout(onComplete, 600);
        return () => clearTimeout(timer);
    }, [onComplete]);
    return <div className="shockwave" style={{ left: x, top: y, '--shockwave-color': color }} />;
};

const StarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);

const SpeedIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.415L11 9.586V6z" clipRule="evenodd" />
    </svg>
);

const InfoIcon = ({ onClick }) => (
    <button onClick={onClick} className="absolute top-10 right-0 p-2 rounded-full bg-gray-700/50 hover:bg-gray-600 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    </button>
);

const LeaderboardModal = ({ onClose }) => {
    const [scores, setScores] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchScores = async () => {
            try {
                const db = getFirestore();
                const q = query(collection(db, "leaderboard"), orderBy("score", "desc"), limit(10));
                const querySnapshot = await getDocs(q);
                const leaderboardScores = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setScores(leaderboardScores);
            } catch (error) {
                console.error("Error fetching leaderboard:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchScores();
    }, []);

    return (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-30">
            <div className="bg-gray-800 rounded-2xl p-8 border border-cyan-500 shadow-2xl w-full max-w-md m-4">
                <h2 className="text-3xl font-orbitron text-cyan-400 text-center mb-6">Leaderboard</h2>
                {loading ? <p>Loading...</p> : (
                    <ol className="space-y-3">
                        {scores.map((entry, index) => (
                            <li key={entry.id} className="flex justify-between items-center bg-gray-700/50 p-3 rounded-lg">
                                <span className="text-lg font-bold text-gray-300">
                                    {index + 1}. {entry.initials}
                                    {entry.country && <span className="text-sm text-gray-400 ml-2">({entry.country})</span>}
                                </span>
                                <span className="text-xl font-bold text-yellow-400">{entry.score}</span>
                            </li>
                        ))}
                    </ol>
                )}
                <button onClick={onClose} className="w-full mt-8 py-3 bg-purple-600 hover:bg-purple-700 rounded-full font-bold text-lg transition-colors">
                    Close
                </button>
            </div>
        </div>
    );
};

const PerformanceModal = ({ facetStats, totalLevelsByFacet, totalScore, onClose, userId, onRestart }) => {
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);

    const handleSubmitScore = async (initials, country) => {
        if (!userId) return { success: false, message: "Error: Not signed in." };
        const db = getFirestore();
        const userDocRef = doc(db, "leaderboard", userId);
        try {
            const userDoc = await getDoc(userDocRef);
            if (!userDoc.exists() || userDoc.data().score < totalScore) {
                await setDoc(userDocRef, { initials, score: totalScore, country });
                return { success: true, message: "Score Submitted Successfully!" };
            } else {
                return { success: false, message: `Your existing high score of ${userDoc.data().score} is higher.` };
            }
        } catch (error) {
            console.error("Error submitting score:", error);
            return { success: false, message: "Error submitting score." };
        }
    };
    
    return (
        <>
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-30">
                <div className="bg-gray-800 rounded-2xl p-8 border border-cyan-500 shadow-2xl w-full max-w-md m-4 max-h-[90vh] overflow-y-auto">
                    <h2 className="text-3xl font-orbitron text-cyan-400 text-center mb-2">Performance Review</h2>
                    <div className="text-center mb-6">
                        <p className="text-lg text-gray-300">Total Score</p>
                        <p className="text-5xl font-bold text-yellow-400">{totalScore}</p>
                    </div>
                    <div className="space-y-4">
                        {Object.entries(facetStats).map(([facet, stats]) => {
                            const meaningfulAttempts = stats.correctHits + stats.incorrectHits;
                            const accuracy = meaningfulAttempts > 0 ? Math.round((stats.correctHits / meaningfulAttempts) * 100) : 0;
                            return (
                                <div key={facet} className="bg-gray-700/50 p-4 rounded-lg">
                                    <div className="flex justify-between items-center">
                                        <p className="text-xl font-semibold capitalize text-gray-200">{facet}</p>
                                        <p className={`text-2xl font-bold ${accuracy >= 70 ? 'text-green-400' : accuracy >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>{accuracy}% Correct</p>
                                    </div>
                                    <p className="text-sm text-gray-400 text-right mt-1">
                                        {stats.levelsCompleted} of {totalLevelsByFacet[facet]} levels completed
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex gap-4 mt-8">
                        <button onClick={() => setShowSubmitModal(true)} className="w-full py-3 bg-green-600 hover:bg-green-700 rounded-full font-bold text-lg transition-colors">Submit Score to Leaderboard</button>
                        <button onClick={onClose} className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-full font-bold text-lg transition-colors">Close</button>
                    </div>
                    {showSubmitModal && <SubmitScoreModal onSubmit={handleSubmitScore} onClose={() => setShowSubmitModal(false)} />}
                    <div className="mt-6 pt-6 border-t border-gray-700">
                         <button onClick={() => setShowFeedbackModal(true)} className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded-full font-bold transition-colors">Give Feedback</button>
                    </div>
                </div>
            </div>
            {showFeedbackModal && <FeedbackModal onClose={() => setShowFeedbackModal(false)} />}
        </>
    );
};

const SubmitScoreModal = ({ onSubmit, onClose }) => {
    const [initials, setInitials] = useState('');
    const [country, setCountry] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    const handleLocalSubmit = async () => {
        if (!initials) { setSubmitMessage('Please enter your initials.'); return; }
        setIsSubmitting(true);
        const { success, message } = await onSubmit(initials, country);
        setSubmitMessage(message);
        setIsSubmitting(false);
    };

    return (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-40">
            <div className="bg-gray-700 p-6 rounded-lg text-white w-full max-w-sm">
                <h3 className="text-lg font-bold text-center">Submit to Leaderboard</h3>
                {submitMessage ? (
                    <div className="text-center my-4">
                        <p className={`font-semibold ${submitMessage.includes('Success') ? 'text-green-400' : 'text-yellow-400'}`}>{submitMessage}</p>
                        <button onClick={onClose} className="w-full mt-4 py-2 bg-purple-600 rounded-md">OK</button>
                    </div>
                ) : (
                    <>
                        <input type="text" placeholder="AAA" maxLength="3" value={initials} onChange={e => setInitials(e.target.value.toUpperCase())} className="w-full text-center text-2xl font-bold bg-gray-800 my-4 p-2 rounded-md" />
                        <input type="text" placeholder="Country (Optional)" value={country} onChange={e => setCountry(e.target.value)} className="w-full text-center text-lg bg-gray-800 mb-4 p-2 rounded-md" />
                        <div className="flex gap-4">
                            <button onClick={handleLocalSubmit} disabled={isSubmitting} className="w-full py-2 bg-green-600 rounded-md disabled:bg-gray-500">
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </button>
                            <button onClick={onClose} className="w-full py-2 bg-red-600 rounded-md">Cancel</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// --- NEW: Welcome Modal for role capture ---
const WelcomeModal = ({ onStart }) => {
    const [role, setRole] = useState('');
    const [roleOther, setRoleOther] = useState('');
    const [practiceField, setPracticeField] = useState('');
    const [mode, setMode] = useState('guided');   // 'guided' | 'shuffle'
    const showPracticeField = ['Resident', 'Fellow', 'Consultant / Attending'].includes(role);

    const handleStart = () => {
        const finalRole = role === 'Other' ? roleOther : role;
        const finalField = showPracticeField ? practiceField : '';
        onStart({ role: finalRole, practiceField: finalField, mode });
    };

    return (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-30">
            <div className="bg-gray-800 rounded-2xl p-8 border border-cyan-500 shadow-2xl w-full max-w-md m-4">
                <h2 className="text-2xl font-orbitron text-cyan-400 text-center mb-2">Before You Begin</h2>
                <p className="text-gray-400 text-sm text-center mb-6">
                    Optional: helps us understand how different learners perform. Fully anonymous.
                </p>
                <div className="space-y-3">
                    <select
                        value={role}
                        onChange={e => { setRole(e.target.value); setPracticeField(''); }}
                        className="w-full bg-gray-700 p-3 rounded-md text-white"
                    >
                        <option value="">Select your role (optional)</option>
                        <option>Medical Student</option>
                        <option>Resident</option>
                        <option>Nephrology Fellow</option>
                        <option>Other Fellow</option>
                        <option>Consultant / Attending</option>
                        <option>Nurse / Allied Health</option>
                        <option value="Other">Other</option>
                    </select>
                    {role === 'Other' && (
                        <input
                            type="text"
                            placeholder="Please specify"
                            value={roleOther}
                            onChange={e => setRoleOther(e.target.value)}
                            className="w-full bg-gray-600 p-3 rounded-md placeholder-gray-400 text-white"
                        />
                    )}
                    {showPracticeField && (
                        <select
                            value={practiceField}
                            onChange={e => setPracticeField(e.target.value)}
                            className="w-full bg-gray-700 p-3 rounded-md text-white"
                        >
                            <option value="">Select field (optional)</option>
                            <option>Paediatric Nephrology</option>
                            <option>Adult Nephrology</option>
                            <option>General Paediatrics</option>
                            <option>Internal Medicine</option>
                            <option>Emergency Medicine</option>
                            <option>Family Medicine</option>
                            <option>Critical Care Medicine</option>
                            <option>Other</option>
                        </select>
                    )}
                </div>

                <div className="mt-6">
                    <p className="text-gray-300 text-sm mb-2">How would you like to play? (Level 1 is a tutorial either way.)</p>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => setMode('guided')}
                            className={`py-3 px-2 rounded-md text-sm font-semibold transition-colors ${mode === 'guided' ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                        >
                            Follow the nephron
                            <span className="block text-[11px] font-normal opacity-80">easier to harder (recommended)</span>
                        </button>
                        <button
                            onClick={() => setMode('shuffle')}
                            className={`py-3 px-2 rounded-md text-sm font-semibold transition-colors ${mode === 'shuffle' ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                        >
                            Surprise me
                            <span className="block text-[11px] font-normal opacity-80">random order</span>
                        </button>
                    </div>
                </div>

                <div className="flex gap-3 mt-8">
                    <button
                        onClick={handleStart}
                        className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 rounded-full font-bold text-lg transition-colors"
                    >
                        Let's Go! 🚀
                    </button>
                    <button
                        onClick={() => onStart({ role: '', practiceField: '', mode })}
                        className="w-24 py-3 bg-gray-600 hover:bg-gray-500 rounded-full font-bold transition-colors text-gray-300 text-sm"
                    >
                        Skip
                    </button>
                </div>
            </div>
        </div>
    );
};


// ─── Utility: Kruskal-Wallis H test (pure JS, no library needed) ────────────
function rankData(groups) {
    // Flatten all values with group labels, assign ranks
    const all = groups.flatMap((g, gi) => g.map(v => ({ v, gi })));
    all.sort((a, b) => a.v - b.v);
    const ranked = all.map((item, i) => ({ ...item, rank: i + 1 }));
    // Handle ties — average rank
    let i = 0;
    while (i < ranked.length) {
        let j = i;
        while (j < ranked.length && ranked[j].v === ranked[i].v) j++;
        const avgRank = (ranked.slice(i, j).reduce((s, r) => s + r.rank, 0)) / (j - i);
        for (let k = i; k < j; k++) ranked[k].rank = avgRank;
        i = j;
    }
    const N = ranked.length;
    const groupRankSums = groups.map((_, gi) =>
        ranked.filter(r => r.gi === gi).reduce((s, r) => s + r.rank, 0)
    );
    const H = (12 / (N * (N + 1))) *
        groups.reduce((s, g, gi) => s + (groupRankSums[gi] ** 2) / g.length, 0) -
        3 * (N + 1);
    const df = groups.length - 1;
    // Chi-squared p-value approximation (Wilson-Hilferty)
    const pValue = H <= 0 ? 1 : chiSquaredP(H, df);
    return { H: +H.toFixed(3), df, pValue: +pValue.toFixed(4), groupRankSums };
}

function chiSquaredP(x, df) {
    // Regularised incomplete gamma function via series expansion
    const a = df / 2;
    const x2 = x / 2;
    if (x2 <= 0) return 1;
    // Use complement: P(chi > x) = 1 - regularisedGammaP(a, x/2)
    return 1 - regularisedGammaP(a, x2);
}

function regularisedGammaP(a, x) {
    if (x < 0) return 0;
    if (x === 0) return 0;
    // Series expansion
    let sum = 1 / a;
    let term = 1 / a;
    for (let n = 1; n < 200; n++) {
        term *= x / (a + n);
        sum += term;
        if (Math.abs(term) < 1e-10) break;
    }
    return Math.min(1, sum * Math.exp(-x + a * Math.log(x) - logGamma(a)));
}

function logGamma(z) {
    // Stirling approximation
    const c = [76.18009172947146,-86.50532032941677,24.01409824083091,
                -1.231739572450155,0.1208650973866179e-2,-0.5395239384953e-5];
    let y = z, x = z, tmp = x + 5.5;
    tmp -= (x + 0.5) * Math.log(tmp);
    let ser = 1.000000000190015;
    for (let j = 0; j < 6; j++) ser += c[j] / ++y;
    return -tmp + Math.log(2.5066282746310005 * ser / x);
}

function mean(arr) { return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null; }
function median(arr) {
    if (!arr.length) return null;
    const s = [...arr].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}
function stdDev(arr) {
    if (arr.length < 2) return null;
    const m = mean(arr);
    return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / (arr.length - 1));
}

const ADMIN_PASSWORD = "Tubulopathy1989";
const ROLES_ORDER = ["Medical Student","Resident","Nephrology Fellow","Other Fellow","Consultant / Attending","Nurse / Allied Health","Other",""];
const FACET_COLOURS = { onboarding: "#6b7280", anatomy: "#38bdf8", diuretics: "#a78bfa", tubulopathies: "#f97316", genetics: "#4ade80" };
const ROLE_COLOURS = ["#38bdf8","#a78bfa","#f97316","#4ade80","#f43f5e","#facc15","#94a3b8","#cbd5e1"];

// ─── Admin Dashboard ─────────────────────────────────────────────────────────
const AdminDashboard = ({ onClose }) => {
    const [authed, setAuthed] = useState(false);
    const [pwInput, setPwInput] = useState('');
    const [pwError, setPwError] = useState('');
    const [rawData, setRawData] = useState([]);       // raw levelAttempts docs
    const [rawSessions, setRawSessions] = useState([]); // performanceData docs
    const [loading, setLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [modeFilter, setModeFilter] = useState('all');   // 'all' | 'guided' | 'shuffle'

    const handleLogin = () => {
        if (pwInput === ADMIN_PASSWORD) { setAuthed(true); } 
        else { setPwError('Incorrect password.'); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const db = getFirestore();
            const [attSnap, sessSnap] = await Promise.all([
                getDocs(collection(db, "levelAttempts")),
                getDocs(collection(db, "performanceData")),
            ]);
            setRawData(attSnap.docs.map(d => d.data()));
            setRawSessions(sessSnap.docs.map(d => d.data()));
            setLastRefresh(new Date());
        } catch(e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { if (authed) fetchData(); }, [authed]);

    // Global stratifier: every view below reads these, so filtering by mode re-slices the whole dashboard.
    const data = useMemo(
        () => modeFilter === 'all' ? rawData : rawData.filter(d => (d.mode || '') === modeFilter),
        [rawData, modeFilter]);
    const sessions = useMemo(
        () => modeFilter === 'all' ? rawSessions : rawSessions.filter(s => (s.mode || '') === modeFilter),
        [rawSessions, modeFilter]);

    // ── Derived stats ─────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        if (!data.length) return null;

        const roles = [...new Set(data.map(d => d.role || ''))].sort((a,b) => 
            ROLES_ORDER.indexOf(a) - ROLES_ORDER.indexOf(b));
        const facets = ['anatomy','diuretics','tubulopathies','genetics']; // 'onboarding' (level 1) excluded from all comparative analyses

        // Per-level accuracy summary
        const levelSummary = {};
        for (let l = 1; l <= 23; l++) {
            const rows = data.filter(d => d.levelNum === l);
            const accs = rows.filter(d => d.accuracy != null).map(d => d.accuracy);
            levelSummary[l] = {
                n: accs.length,
                nSeen: rows.length,
                nSkipped: rows.filter(d => d.status === 'skipped').length,
                meanAcc: mean(accs),
                medianAcc: median(accs),
                sd: stdDev(accs),
                facet: levels[l]?.facet,
            };
        }

        // Per-facet per-role accuracy
        const facetRoleMatrix = {};
        facets.forEach(f => {
            facetRoleMatrix[f] = {};
            roles.forEach(r => {
                const accs = data.filter(d => d.facet === f && (d.role || '') === r && d.accuracy != null)
                    .map(d => d.accuracy);
                facetRoleMatrix[f][r] = { accs, mean: mean(accs), n: accs.length };
            });
        });

        // Kruskal-Wallis per facet
        const kwResults = {};
        facets.forEach(f => {
            const groups = roles.map(r => facetRoleMatrix[f][r].accs).filter(g => g.length >= 3);
            if (groups.length >= 2) {
                kwResults[f] = rankData(groups);
            }
        });

        // Level difficulty chart data
        const levelChartData = Object.entries(levelSummary).map(([l, s]) => ({
            level: `L${l}`,
            levelNum: +l,
            accuracy: s.meanAcc !== null ? +s.meanAcc.toFixed(1) : null,
            n: s.n,
            facet: s.facet,
        })).filter(d => d.accuracy != null);

        // Role breakdown chart per facet
        const facetRoleChartData = facets.map(f => {
            const row = { facet: f };
            roles.forEach(r => { 
                if (r && facetRoleMatrix[f][r].n > 0)
                    row[r] = facetRoleMatrix[f][r].mean !== null 
                        ? +facetRoleMatrix[f][r].mean.toFixed(1) : null; 
            });
            return row;
        });

        // Session summary
        const totalSessions = sessions.length;
        const withRole = sessions.filter(s => s.role).length;
        const roleCounts = {};
        sessions.forEach(s => { const r = s.role || 'Not specified'; roleCounts[r] = (roleCounts[r]||0)+1; });
        const roleCountsChart = Object.entries(roleCounts)
            .sort((a,b) => b[1]-a[1])
            .map(([role, count]) => ({ role: role || 'Not specified', count }));

        // Completion rate per level (% of all sessions that reached that level)
        const completionChart = Object.entries(levelSummary).map(([l, s]) => ({
            level: `L${l}`,
            pct: totalSessions > 0 ? +((s.n / totalSessions) * 100).toFixed(1) : 0,
            facet: s.facet,
        }));

        return { roles, facets, levelSummary, facetRoleMatrix, kwResults,
                 levelChartData, facetRoleChartData, totalSessions, withRole,
                 roleCountsChart, completionChart };
    }, [data, sessions]);

    // ── Login screen ─────────────────────────────────────────────────────────
    if (!authed) return (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-600 w-full max-w-sm m-4">
                <h2 className="text-xl font-orbitron text-cyan-400 text-center mb-6">Admin Access</h2>
                <input
                    type="password" placeholder="Password"
                    value={pwInput} onChange={e => { setPwInput(e.target.value); setPwError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    className="w-full bg-gray-700 p-3 rounded-md text-white mb-3 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    autoFocus
                />
                {pwError && <p className="text-red-400 text-sm mb-3">{pwError}</p>}
                <div className="flex gap-3">
                    <button onClick={handleLogin} className="flex-1 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-full font-bold transition-colors">Login</button>
                    <button onClick={onClose} className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 rounded-full font-bold transition-colors">Cancel</button>
                </div>
            </div>
        </div>
    );

    // ── Dashboard ─────────────────────────────────────────────────────────────
    const tabs = ['overview','levels','facets','stats','raw'];

    const pBadge = (p) => {
        if (p < 0.001) return <span className="ml-2 px-2 py-0.5 bg-green-700 text-green-100 text-xs rounded-full">p&lt;0.001 ***</span>;
        if (p < 0.01)  return <span className="ml-2 px-2 py-0.5 bg-green-700 text-green-100 text-xs rounded-full">p={p} **</span>;
        if (p < 0.05)  return <span className="ml-2 px-2 py-0.5 bg-yellow-700 text-yellow-100 text-xs rounded-full">p={p} *</span>;
        return <span className="ml-2 px-2 py-0.5 bg-gray-600 text-gray-300 text-xs rounded-full">p={p} ns</span>;
    };

    return (
        <div className="fixed inset-0 bg-gray-950 z-50 overflow-y-auto font-poppins">
            <div className="max-w-6xl mx-auto p-4 sm:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-orbitron text-cyan-400">ENaC Admin Dashboard</h1>
                        {lastRefresh && <p className="text-xs text-gray-500 mt-1">Last updated: {lastRefresh.toLocaleTimeString()}</p>}
                    </div>
                    <div className="flex gap-2 items-center">
                        <label className="text-xs text-gray-400">Mode</label>
                        <select value={modeFilter} onChange={e => setModeFilter(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded-full text-sm px-3 py-2 text-gray-200">
                            <option value="all">All modes</option>
                            <option value="guided">Guided</option>
                            <option value="shuffle">Shuffle</option>
                        </select>
                        <button onClick={fetchData} disabled={loading}
                            className="px-4 py-2 bg-cyan-700 hover:bg-cyan-600 disabled:bg-gray-700 rounded-full text-sm font-bold transition-colors">
                            {loading ? '⟳ Loading…' : '⟳ Refresh'}
                        </button>
                        <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-full text-sm font-bold transition-colors">✕ Close</button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-6 flex-wrap">
                    {tabs.map(t => (
                        <button key={t} onClick={() => setActiveTab(t)}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize transition-colors ${activeTab===t ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                            {t}
                        </button>
                    ))}
                </div>

                {loading && <div className="text-center py-20 text-gray-400">Loading data…</div>}
                {!loading && !stats && <div className="text-center py-20 text-gray-500">No data yet.</div>}
                {!loading && stats && (
                    <>
                    {/* ── OVERVIEW ── */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* KPI cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                {[
                                    { label: 'Total Sessions', value: stats.totalSessions },
                                    { label: 'Level Attempts', value: data.length },
                                    { label: 'Sessions w/ Role', value: stats.withRole },
                                    { label: 'Unique Roles', value: stats.roles.filter(r=>r).length },
                                ].map(k => (
                                    <div key={k.label} className="bg-gray-800 rounded-xl p-4 border border-gray-700 text-center">
                                        <p className="text-3xl font-bold text-cyan-400">{k.value}</p>
                                        <p className="text-xs text-gray-400 mt-1">{k.label}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Role distribution */}
                            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                                <h3 className="text-lg font-semibold text-white mb-1">Sessions by Role</h3>
                                <p className="text-xs text-gray-400 mb-4">Number of game sessions initiated per role category.</p>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={stats.roleCountsChart} layout="vertical" margin={{left:20,right:20}}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false}/>
                                        <XAxis type="number" tick={{fill:'#9ca3af',fontSize:11}} />
                                        <YAxis type="category" dataKey="role" tick={{fill:'#d1d5db',fontSize:11}} width={160}/>
                                        <Tooltip contentStyle={{backgroundColor:'#1f2937',border:'1px solid #374151',borderRadius:'8px'}} />
                                        <Bar dataKey="count" fill="#38bdf8" radius={[0,4,4,0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Completion funnel */}
                            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                                <h3 className="text-lg font-semibold text-white mb-1">Level Reach Rate</h3>
                                <p className="text-xs text-gray-400 mb-4">Percentage of all sessions that completed each level. Shows where users drop off. <span className="text-yellow-500">L1 = onboarding (excluded from domain analyses).</span></p>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={stats.completionChart} margin={{left:0,right:10}}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false}/>
                                        <XAxis dataKey="level" tick={{fill:'#9ca3af',fontSize:10}}/>
                                        <YAxis tick={{fill:'#9ca3af',fontSize:11}} unit="%" domain={[0,100]}/>
                                        <Tooltip contentStyle={{backgroundColor:'#1f2937',border:'1px solid #374151',borderRadius:'8px'}} formatter={v => v+'%'}/>
                                        <Bar dataKey="pct" radius={[4,4,0,0]}>
                                            {stats.completionChart.map((entry,i) => (
                                                <Cell key={i} fill={FACET_COLOURS[entry.facet] || '#6b7280'}/>
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                                <div className="flex flex-wrap gap-3 mt-2 justify-center">
                                    {Object.entries(FACET_COLOURS).map(([f,c]) => (
                                        <span key={f} className="text-xs text-gray-400 flex items-center gap-1">
                                            <span style={{background:c}} className="inline-block w-3 h-3 rounded-sm"/> 
                                            {f}{f === 'onboarding' ? ' (excluded from stats)' : ''}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── LEVELS ── */}
                    {activeTab === 'levels' && (
                        <div className="space-y-6">
                            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                                <h3 className="text-lg font-semibold text-white mb-1">Mean Accuracy per Level</h3>
                                <p className="text-xs text-gray-400 mb-4">Average % correct hits ÷ total attempts per level. Colour = domain. Lower bars = harder levels.</p>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={stats.levelChartData} margin={{left:0,right:10}}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false}/>
                                        <XAxis dataKey="level" tick={{fill:'#9ca3af',fontSize:10}}/>
                                        <YAxis tick={{fill:'#9ca3af',fontSize:11}} unit="%" domain={[0,100]}/>
                                        <Tooltip contentStyle={{backgroundColor:'#1f2937',border:'1px solid #374151',borderRadius:'8px'}}
                                            formatter={(v,n,p) => [`${v}% (n=${p.payload.n})`, 'Mean accuracy']}/>
                                        <Bar dataKey="accuracy" radius={[4,4,0,0]}>
                                            {stats.levelChartData.map((entry,i) => (
                                                <Cell key={i} fill={FACET_COLOURS[entry.facet] || '#6b7280'}/>
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Level table */}
                            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700 overflow-x-auto">
                                <h3 className="text-lg font-semibold text-white mb-1">Level Summary Table</h3>
                                <p className="text-xs text-gray-400 mb-4">n = number of attempts. SD = standard deviation of accuracy scores.</p>
                                <table className="w-full text-sm text-left">
                                    <thead>
                                        <tr className="text-gray-400 border-b border-gray-700">
                                            <th className="pb-2 pr-4">Level</th>
                                            <th className="pb-2 pr-4">Domain</th>
                                            <th className="pb-2 pr-4">n</th>
                                            <th className="pb-2 pr-4">Mean %</th>
                                            <th className="pb-2 pr-4">Median %</th>
                                            <th className="pb-2">SD</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(stats.levelSummary).map(([l,s]) => (
                                            <tr key={l} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                                                <td className="py-1.5 pr-4 font-mono text-cyan-300">L{l}</td>
                                                <td className="py-1.5 pr-4">
                                                    <span style={{color: FACET_COLOURS[s.facet]}} className="capitalize font-semibold">{s.facet}</span>
                                                </td>
                                                <td className="py-1.5 pr-4 text-gray-300">{s.n}</td>
                                                <td className="py-1.5 pr-4 font-bold" style={{color: s.meanAcc > 70 ? '#4ade80' : s.meanAcc > 40 ? '#facc15' : '#f87171'}}>
                                                    {s.meanAcc !== null ? s.meanAcc.toFixed(1)+'%' : '—'}
                                                </td>
                                                <td className="py-1.5 pr-4 text-gray-300">{s.medianAcc !== null ? s.medianAcc.toFixed(1)+'%' : '—'}</td>
                                                <td className="py-1.5 text-gray-400">{s.sd !== null ? s.sd.toFixed(1) : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ── FACETS ── */}
                    {activeTab === 'facets' && (
                        <div className="space-y-6">
                            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                                <h3 className="text-lg font-semibold text-white mb-1">Mean Accuracy by Domain × Role</h3>
                                <p className="text-xs text-gray-400 mb-4">Each cluster of bars is a domain. Each bar within the cluster is a role. Only roles with ≥1 attempt shown.</p>
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={stats.facetRoleChartData} margin={{left:0,right:10}}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false}/>
                                        <XAxis dataKey="facet" tick={{fill:'#d1d5db',fontSize:12}} />
                                        <YAxis tick={{fill:'#9ca3af',fontSize:11}} unit="%" domain={[0,100]}/>
                                        <Tooltip contentStyle={{backgroundColor:'#1f2937',border:'1px solid #374151',borderRadius:'8px'}} formatter={v => v !== null ? v+'%' : 'n/a'}/>
                                        <Legend wrapperStyle={{fontSize:'11px',color:'#9ca3af'}} />
                                        {stats.roles.filter(r=>r).map((r,i) => (
                                            <Bar key={r} dataKey={r} fill={ROLE_COLOURS[i % ROLE_COLOURS.length]} radius={[3,3,0,0]}/>
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Per-facet accuracy tables */}
                            {stats.facets.map(f => (
                                <div key={f} className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                                    <h3 className="text-lg font-semibold capitalize mb-1" style={{color: FACET_COLOURS[f]}}>{f}</h3>
                                    <p className="text-xs text-gray-400 mb-3">Mean accuracy (%) per role for all {f} levels combined.</p>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left">
                                            <thead>
                                                <tr className="text-gray-400 border-b border-gray-700">
                                                    <th className="pb-2 pr-6">Role</th>
                                                    <th className="pb-2 pr-6">n</th>
                                                    <th className="pb-2 pr-6">Mean %</th>
                                                    <th className="pb-2">Distribution</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {stats.roles.map(r => {
                                                    const d = stats.facetRoleMatrix[f][r];
                                                    if (!d || d.n === 0) return null;
                                                    return (
                                                        <tr key={r||'unspecified'} className="border-b border-gray-700/50">
                                                            <td className="py-1.5 pr-6 text-gray-200">{r || 'Not specified'}</td>
                                                            <td className="py-1.5 pr-6 text-gray-400">{d.n}</td>
                                                            <td className="py-1.5 pr-6 font-bold" style={{color: d.mean > 70 ? '#4ade80' : d.mean > 40 ? '#facc15' : '#f87171'}}>
                                                                {d.mean !== null ? d.mean.toFixed(1)+'%' : '—'}
                                                            </td>
                                                            <td className="py-1.5">
                                                                <div className="w-32 bg-gray-700 rounded-full h-2">
                                                                    <div className="h-2 rounded-full" style={{width: `${d.mean||0}%`, background: FACET_COLOURS[f]}}/>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── STATS ── */}
                    {activeTab === 'stats' && (
                        <div className="space-y-6">
                            <div className="bg-blue-900/30 border border-blue-700/50 rounded-xl p-5">
                                <h3 className="text-base font-semibold text-blue-300 mb-2">How to read these tests</h3>
                                <p className="text-sm text-gray-300 leading-relaxed">
                                    The <strong className="text-white">Kruskal-Wallis H test</strong> is a non-parametric test that asks: "do accuracy scores 
                                    differ significantly across role groups within this domain?" It makes no assumptions about 
                                    normality, which is appropriate here since accuracy distributions are often skewed. 
                                    A significant result (p&lt;0.05) tells you the groups are not all equivalent — 
                                    but not <em>which</em> groups differ. For pairwise follow-up you would use Mann-Whitney U 
                                    with Bonferroni correction. <strong className="text-white">Minimum ~5 per group</strong> is 
                                    needed for the p-value approximation to be reliable.
                                </p>
                            </div>

                            {stats.facets.map(f => {
                                const kw = stats.kwResults[f];
                                const rolesWithData = stats.roles.filter(r => stats.facetRoleMatrix[f][r]?.n >= 1);
                                return (
                                    <div key={f} className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                                        <div className="flex items-center gap-3 mb-3">
                                            <h3 className="text-lg font-semibold capitalize" style={{color:FACET_COLOURS[f]}}>{f}</h3>
                                            {kw ? pBadge(kw.pValue) : <span className="text-xs text-gray-500">Insufficient data (need ≥3 groups with ≥3 attempts each)</span>}
                                        </div>
                                        {kw && (
                                            <p className="text-sm text-gray-400 mb-4">
                                                H({kw.df}) = {kw.H}, p = {kw.pValue}. 
                                                {kw.pValue < 0.05 
                                                    ? ' Significant difference in accuracy across roles. Consider pairwise Mann-Whitney comparisons to identify which groups differ.'
                                                    : ' No statistically significant difference across roles detected at this sample size.'}
                                            </p>
                                        )}
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-gray-400 border-b border-gray-700 text-left">
                                                        <th className="pb-2 pr-4">Role</th>
                                                        <th className="pb-2 pr-4">n</th>
                                                        <th className="pb-2 pr-4">Mean %</th>
                                                        <th className="pb-2 pr-4">Median %</th>
                                                        <th className="pb-2">SD</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rolesWithData.map(r => {
                                                        const d = stats.facetRoleMatrix[f][r];
                                                        const m = mean(d.accs), med = median(d.accs), sd = stdDev(d.accs);
                                                        return (
                                                            <tr key={r||'ns'} className="border-b border-gray-700/50">
                                                                <td className="py-1.5 pr-4 text-gray-200">{r||'Not specified'}</td>
                                                                <td className="py-1.5 pr-4 text-gray-400">{d.n}</td>
                                                                <td className="py-1.5 pr-4 font-bold" style={{color: m>70?'#4ade80':m>40?'#facc15':'#f87171'}}>{m!==null?m.toFixed(1)+'%':'—'}</td>
                                                                <td className="py-1.5 pr-4 text-gray-300">{med!==null?med.toFixed(1)+'%':'—'}</td>
                                                                <td className="py-1.5 text-gray-400">{sd!==null?sd.toFixed(1):'—'}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })}

                            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                                <h3 className="text-lg font-semibold text-white mb-2">Cronbach's Alpha per Domain</h3>
                                <p className="text-xs text-gray-400 mb-4">
                                    Measures internal consistency — whether levels within a domain 
                                    are measuring the same underlying construct. α&gt;0.7 = acceptable, &gt;0.8 = good. 
                                    Computed across users who attempted all levels within the domain.
                                </p>
                                {stats.facets.map(f => {
                                    // Cronbach's alpha: levels in domain as items, users as subjects
                                    const facetLevels = Object.entries(levels).filter(([,v])=>v.facet===f).map(([k])=>+k);
                                    const usersWithAll = [...new Set(data.filter(d=>d.facet===f).map(d=>d.userId))]
                                        .filter(uid => facetLevels.every(l => data.some(d=>d.userId===uid && d.levelNum===l)));
                                    const matrix = usersWithAll.map(uid =>
                                        facetLevels.map(l => {
                                            const row = data.find(d=>d.userId===uid && d.levelNum===l);
                                            return row?.accuracy ?? null;
                                        }).filter(v=>v!==null)
                                    ).filter(row => row.length === facetLevels.length);

                                    let alpha = null;
                                    if (matrix.length >= 2 && facetLevels.length >= 2) {
                                        const k = facetLevels.length;
                                        const itemVars = facetLevels.map((_, i) => {
                                            const col = matrix.map(r => r[i]);
                                            return stdDev(col) ** 2;
                                        });
                                        const totalVar = stdDev(matrix.map(r => r.reduce((a,b)=>a+b,0))) ** 2;
                                        alpha = totalVar > 0 ? +(k/(k-1) * (1 - itemVars.reduce((a,b)=>a+b,0)/totalVar)).toFixed(3) : null;
                                    }

                                    return (
                                        <div key={f} className="flex items-center justify-between py-2 border-b border-gray-700/50 last:border-0">
                                            <span className="capitalize font-semibold" style={{color:FACET_COLOURS[f]}}>{f}</span>
                                            <span className="text-sm text-gray-400">{matrix.length} users with complete data</span>
                                            <span className="font-bold text-lg" style={{color: alpha===null?'#6b7280':alpha>0.8?'#4ade80':alpha>0.7?'#facc15':'#f87171'}}>
                                                {alpha !== null ? `α = ${alpha}` : 'Insufficient data'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── RAW ── */}
                    {activeTab === 'raw' && (
                        <div className="space-y-4">
                            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                                <h3 className="text-lg font-semibold text-white mb-1">Raw Level Attempts</h3>
                                <p className="text-xs text-gray-400 mb-4">Most recent {Math.min(data.length, 200)} records from the levelAttempts collection. Export from Firebase Console for full dataset.</p>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs text-left">
                                        <thead>
                                            <tr className="text-gray-400 border-b border-gray-700">
                                                {['userId','role','practiceField','levelNum','facet','position','mode','status','correctHits','incorrectHits','accuracy'].map(h => (
                                                    <th key={h} className="pb-2 pr-4 font-semibold">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...data].sort((a,b) => (b.completedAt?.seconds||0)-(a.completedAt?.seconds||0))
                                                .slice(0,200).map((row,i) => (
                                                <tr key={i} className="border-b border-gray-700/30 hover:bg-gray-700/20">
                                                    <td className="py-1 pr-4 font-mono text-gray-500 text-[10px]">{(row.userId||'').slice(0,8)}…</td>
                                                    <td className="py-1 pr-4 text-gray-300">{row.role||'—'}</td>
                                                    <td className="py-1 pr-4 text-gray-400">{row.practiceField||'—'}</td>
                                                    <td className="py-1 pr-4 text-cyan-300">{row.levelNum}</td>
                                                    <td className="py-1 pr-4" style={{color:FACET_COLOURS[row.facet]}}>{row.facet}</td>
                                                    <td className="py-1 pr-4 text-gray-400">{row.position ?? '—'}</td>
                                                    <td className="py-1 pr-4 text-gray-400">{row.mode || '—'}</td>
                                                    <td className="py-1 pr-4" style={{color: row.status === 'skipped' ? '#facc15' : '#9ca3af'}}>{row.status || '—'}</td>
                                                    <td className="py-1 pr-4 text-green-400">{row.correctHits ?? '—'}</td>
                                                    <td className="py-1 pr-4 text-red-400">{row.incorrectHits ?? '—'}</td>
                                                    <td className="py-1 font-bold" style={{color: row.accuracy>70?'#4ade80':row.accuracy>40?'#facc15':'#f87171'}}>
                                                        {row.accuracy != null ? row.accuracy+'%' : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                    </>
                )}
                <p className="text-center text-xs text-gray-700 mt-8">ENaC Admin — not for distribution</p>
            </div>
        </div>
    );
};

const IntroAnimation = ({ onStartGame, onShowLeaderboard, onShowInstructions, onShowAdmin }) => {
    return (
        <div className="w-full h-screen bg-gray-900 flex flex-col items-center justify-center overflow-hidden p-4">
            <div className="flex-grow flex flex-col items-center justify-center">
                <svg viewBox="0 0 400 200" className="w-full max-w-lg h-auto">
                    <path 
                        id="intro-path"
                        d="M 20 100 C 80 20, 150 180, 250 100 S 380 20, 380 100"
                        stroke="#ff9800"
                        strokeWidth="10"
                        fill="none"
                        strokeLinecap="round"
                        className="intro-path-draw"
                    />
                    <circle cx="0" cy="0" r="10" fill="#38bdf8" className="intro-dot-travel">
                        <animateMotion dur="3s" begin="0.5s" fill="freeze" repeatCount="1">
                            <mpath href="#intro-path" />
                        </animateMotion>
                    </circle>
                </svg>
                <div className="text-center intro-title-fade">
                    <h1 className="text-6xl font-bold text-cyan-400 font-orbitron">ENaC</h1>
                    <h2 className="text-2xl text-gray-400 font-orbitron mt-2">Electronic Nephron Anatomy Challenge</h2>
                </div>
                <div className="flex flex-wrap justify-center gap-4 intro-button-fade">
                    <button onClick={onStartGame} className="mt-8 px-10 py-4 bg-purple-600 hover:bg-purple-700 rounded-full font-bold text-xl transition-all shadow-lg">Begin</button>
                    <button onClick={onShowInstructions} className="mt-8 px-10 py-4 bg-blue-600 hover:bg-blue-700 rounded-full font-bold text-xl transition-all shadow-lg">How to Play</button>
                    <button onClick={onShowLeaderboard} className="mt-8 px-10 py-4 bg-gray-600 hover:bg-gray-700 rounded-full font-bold text-xl transition-all shadow-lg">Leaderboard</button>
                </div>
            </div>
            <footer className="text-center text-xs text-gray-500 py-4 intro-button-fade">
                <p>Created by Dr. Amrit Kirpalani, MD, MEHP, FRCPC.</p>
                <p>For Educational Purposes only. Not to be used for clinical decision-making. Licensed under CC BY-NC-SA 4.0.</p>
                <button onClick={onShowAdmin} className="mt-2 text-gray-700 hover:text-gray-500 text-[10px] transition-colors">admin</button>
            </footer>
        </div>
    );
};

const InstructionsModal = ({ onClose }) => {
    return (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-30">
            <div className="bg-gray-800 rounded-2xl p-8 border border-cyan-500 shadow-2xl w-full max-w-lg m-4 text-gray-300">
                <h2 className="text-3xl font-orbitron text-cyan-400 text-center mb-6">How to Play</h2>
                <div className="space-y-4 text-left">
                    <div>
                        <h3 className="text-xl font-bold text-white mb-1">Gameplay</h3>
                        <p>The goal is to test your knowledge of renal physiology. A blue dot will travel through the nephron. Click the correct button while the dot is over the corresponding segment. Your score is based on timing and accuracy.</p>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white mb-1">Purpose & Disclaimer</h3>
                        <p>This game provides a high-level overview of tubular anatomy and physiology. It is designed to reinforce foundational knowledge and cannot capture all clinical nuance. This tool is for <strong className="font-bold">educational purposes only</strong> and is not to be used for clinical decision-making.</p>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white mb-1">Give Feedback</h3>
                        <p>You can choose to give anonymous feedback (encouraged) to help contribute to refinement of this tool and ongoing education quality improvement in nephrology.</p>
                    </div>
                </div>
                <button onClick={onClose} className="w-full mt-8 py-3 bg-purple-600 hover:bg-purple-700 rounded-full font-bold text-lg transition-colors">
                    Got it!
                </button>
            </div>
        </div>
    );
};

const FeedbackModal = ({ onClose }) => {
    const [ratings, setRatings] = useState({ q1: 0, q2: 0, q3: 0 });
    const [comment, setComment] = useState('');
    const [role, setRole] = useState('');
    const [roleOther, setRoleOther] = useState('');
    const [practiceField, setPracticeField] = useState('');
    const [practiceFieldOther, setPracticeFieldOther] = useState('');
    const [country, setCountry] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const showPracticeField = ['Resident', 'Fellow', 'Attending'].includes(role);

    const handleRating = (question, rating) => {
        setRatings(prev => ({ ...prev, [question]: rating }));
    };

    const handleSubmit = async () => {
        const db = getFirestore();
        await addDoc(collection(db, "feedback"), {
            ratings,
            comment,
            role: role === 'Other' ? roleOther : role,
            practiceField: showPracticeField ? (practiceField === 'Other' ? practiceFieldOther : practiceField) : '',
            country,
            timestamp: new Date(),
        });
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-50">
                <div className="bg-gray-800 rounded-2xl p-8 border border-cyan-500 shadow-2xl w-full max-w-md m-4 text-center">
                    <h2 className="text-2xl font-bold text-green-400 mb-4">Thank you for your feedback!</h2>
                    <button onClick={onClose} className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-full font-bold text-lg transition-colors">Close</button>
                </div>
            </div>
        );
    }

    return (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-2xl p-8 border border-cyan-500 shadow-2xl w-full max-w-lg m-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-3xl font-orbitron text-cyan-400 text-center mb-6">Give Feedback</h2>
                <div className="space-y-6">
                    <div>
                        <p className="font-semibold mb-1">1. How useful was this game as a learning tool?</p>
                        <div className="flex justify-between items-center text-xs text-gray-400 px-1"><span>Not at all</span><span>Very useful</span></div>
                        <div className="flex gap-2 mt-1">
                            {[1,2,3,4,5].map(r => <button key={r} onClick={() => handleRating('q1', r)} className={`w-full py-2 rounded-md transition-colors ${ratings.q1 === r ? 'bg-cyan-500' : 'bg-gray-600 hover:bg-gray-500'}`}>{r}</button>)}
                        </div>
                    </div>
                    <div>
                        <p className="font-semibold mb-1">2. How likely are you to play this game again?</p>
                        <div className="flex justify-between items-center text-xs text-gray-400 px-1"><span>Not at all</span><span>Very likely</span></div>
                        <div className="flex gap-2 mt-1">
                            {[1,2,3,4,5].map(r => <button key={r} onClick={() => handleRating('q2', r)} className={`w-full py-2 rounded-md transition-colors ${ratings.q2 === r ? 'bg-cyan-500' : 'bg-gray-600 hover:bg-gray-500'}`}>{r}</button>)}
                        </div>
                    </div>
                    <div>
                        <p className="font-semibold mb-1">3. How likely are you to recommend this to someone else?</p>
                        <div className="flex justify-between items-center text-xs text-gray-400 px-1"><span>Not at all</span><span>Very likely</span></div>
                        <div className="flex gap-2 mt-1">
                            {[1,2,3,4,5].map(r => <button key={r} onClick={() => handleRating('q3', r)} className={`w-full py-2 rounded-md transition-colors ${ratings.q3 === r ? 'bg-cyan-500' : 'bg-gray-600 hover:bg-gray-500'}`}>{r}</button>)}
                        </div>
                    </div>
                    <div>
                        <p className="font-semibold mb-1">4. Any other comments? (Optional)</p>
                        <textarea value={comment} onChange={e => setComment(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md h-24" />
                    </div>
                    <div className="space-y-4 pt-4 border-t border-gray-700">
                        <p className="font-semibold text-center">Optional Demographics</p>
                        <select value={role} onChange={e => setRole(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                            <option value="">Select your Role...</option>
                            <option>Undergraduate Student</option>
                            <option>Graduate Student</option>
                            <option>Medical Student</option>
                            <option>Nurse</option>
                            <option>Allied Health Professional</option>
                            <option>Resident</option>
                            <option>Fellow</option>
                            <option>Attending</option>
                            <option value="Other">Other</option>
                        </select>
                        {role === 'Other' && (
                            <input type="text" placeholder="Please specify role" value={roleOther} onChange={e => setRoleOther(e.target.value)} className="w-full bg-gray-600 p-2 rounded-md placeholder-gray-400" />
                        )}
                        {showPracticeField && (
                            <>
                                <select value={practiceField} onChange={e => setPracticeField(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md">
                                    <option value="">Select Field of Practice...</option>
                                    <option>Paediatric Nephrology</option>
                                    <option>Adult Nephrology</option>
                                    <option>General Paediatrics</option>
                                    <option>Internal Medicine</option>
                                    <option>Emergency Medicine</option>
                                    <option>Family Medicine</option>
                                    <option>Critical Care Medicine</option>
                                    <option value="Other">Other</option>
                                </select>
                                {practiceField === 'Other' && (
                                    <input type="text" placeholder="Please specify field" value={practiceFieldOther} onChange={e => setPracticeFieldOther(e.target.value)} className="w-full bg-gray-600 p-2 rounded-md placeholder-gray-400" />
                                )}
                            </>
                        )}
                        <input type="text" placeholder="Country (optional)" value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-gray-700 p-2 rounded-md placeholder-gray-400" />
                    </div>
                </div>
                <div className="flex gap-4 mt-8">
                    <button onClick={handleSubmit} className="w-full py-3 bg-green-600 hover:bg-green-700 rounded-full font-bold text-lg transition-colors">Submit Feedback</button>
                    <button onClick={onClose} className="w-full py-3 bg-gray-600 hover:bg-gray-700 rounded-full font-bold text-lg transition-colors">Cancel</button>
                </div>
            </div>
        </div>
    );
};

const isLevelComplete = (cfg, disabledButtons) => {
    if (Array.isArray(cfg.map?.Identify)) return false;
    if (cfg.map) {
        const requiredButtons = Object.keys(cfg.map);
        return requiredButtons.every(key => disabledButtons.includes(key));
    }
    return cfg.buttons.every(btn => disabledButtons.includes(btn));
};

// Write base session doc immediately when game starts — captures role even if player quits
const initSessionData = async (userId, roleData) => {
    if (!userId) return;
    const db = getFirestore();
    const ref = doc(db, "performanceData", userId);
    try {
        await setDoc(ref, {
            userId,
            sessionStarted: new Date(),
            gameVersion: GAME_VERSION,
            role: roleData?.role || '',
            practiceField: roleData?.practiceField || '',
            mode: roleData?.mode || '',
            lastSessionId: roleData?.sessionId || '',
        }, { merge: true });
    } catch (e) {
        console.error("Error initialising session:", e);
    }
};

// Write a single completed level:
//   1. Updates the summary performanceData doc (for leaderboard/total score)
//   2. Writes a flat levelAttempts doc (one row per user per level — easy CSV export for analysis)
const submitLevelData = async (userId, levelNum, levelData, currentScore, roleData, meta = {}) => {
    if (!userId) return;
    const db = getFirestore();
    const correctHits = levelData.correctHits || 0;
    const incorrectHits = levelData.incorrectHits || 0;
    const totalAttempts = correctHits + incorrectHits;
    // Outcome state. A level passed through with zero interaction is a SKIP, not a 0% attempt.
    // Skips carry a status only and never populate hit counters or accuracy, so they can never
    // be mistaken for performance or dragged in as 0%.
    const status = totalAttempts > 0 ? 'attempted' : 'skipped';
    const accuracy = status === 'attempted'
        ? Math.round((correctHits / totalAttempts) * 100)
        : null;
    const facet = levels[levelNum]?.facet || '';
    const { sessionId = '', mode = '', position = null } = meta;
    try {
        // 1. Rolling summary doc (one per user). Only attempted levels contribute hits;
        //    a skip records its state without writing performance zeros.
        const perfUpdate = {
            finalScore: currentScore,
            lastUpdated: new Date(),
            [`levels.${levelNum}.facet`]: facet,
            [`levels.${levelNum}.status`]: status,
        };
        if (status === 'attempted') {
            perfUpdate[`levels.${levelNum}.correctHits`] = correctHits;
            perfUpdate[`levels.${levelNum}.incorrectHits`] = incorrectHits;
        }
        await setDoc(doc(db, "performanceData", userId), perfUpdate, { merge: true });

        // 2. Flat analytics row — one document per session per level. sessionId is in the id so
        //    replays and separate sessions are preserved rather than overwritten.
        const row = {
            userId,
            sessionId,
            levelNum,
            position,                          // 1-based slot in the presented order
            mode,                              // 'guided' | 'shuffle'
            facet,
            status,                            // 'attempted' | 'skipped'
            role: roleData?.role || '',
            practiceField: roleData?.practiceField || '',
            completedAt: new Date(),
            gameVersion: GAME_VERSION,
        };
        if (status === 'attempted') {
            row.correctHits = correctHits;
            row.incorrectHits = incorrectHits;
            row.totalAttempts = totalAttempts;
            row.accuracy = accuracy;           // 0-100
        }
        await setDoc(doc(db, "levelAttempts", `${userId}_${sessionId}_${levelNum}`), row);
    } catch (e) {
        console.error("Error submitting level data:", e);
    }
};

export default function App() {
    const [showGame, setShowGame] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    const [userRole, setUserRole] = useState({ role: '', practiceField: '' });
    const [level, setLevel] = useState(1);
    const [levelStats, setLevelStats] = useState({});
    const [userId, setUserId] = useState(null);
    const [mode, setMode] = useState('guided');            // session play-order mode
    const [sessionId, setSessionId] = useState('');        // one id per play session
    const [levelSequence, setLevelSequence] = useState(buildLevelSequence('guided')); // presented order
    const [seqIndex, setSeqIndex] = useState(0);           // position within levelSequence

    const [disabledButtons, setDisabledButtons] = useState([]);
    const [hitSegments, setHitSegments] = useState([]);
    const [activeSegment, setActiveSegment] = useState('');
    const [targetSegment, setTargetSegment] = useState('pct');
    const dotPositionRef = React.useRef(START_POS);
    const tubRef = useRef(null);       // the tubule <path>, for getPointAtLength
    const distRef = useRef(0);         // dot's current position along the path (survives speed changes)
    const activeRef = useRef('');      // last active segment (avoids 60fps setState)
    const anchorLenRef = useRef({});   // each segment's path-length position along the tubule
    const wRef = useRef(MAX_INTERACTION_DISTANCE); // half-width of each segment's hit band
    const [message, setMessage] = useState('');
    const [incorrectFlash, setIncorrectFlash] = useState('');
    const [speed, setSpeed] = useState(speedOptions[2]);
    const [effects, setEffects] = useState([]);
    const [showPerfModal, setShowPerfModal] = useState(false);
    const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
    const [showInstructionsModal, setShowInstructionsModal] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const playfieldRef = useRef(null);
    const [isGameOver, setIsGameOver] = useState(false);
    const dotElRef = useRef(null);

    const levelConfig = useMemo(() => levels[level], [level]);

    useEffect(() => {
        const app = initializeApp(firebaseConfig);
        const auth = getAuth(app);
        onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserId(user.uid);
            } else {
                signInAnonymously(auth).catch(error => console.error("Anonymous sign-in failed:", error));
            }
        });
    }, []);

    const { totalScore, facetStats } = useMemo(() => {
        let score = 0;
        const facets = {
            anatomy: { correctHits: 0, incorrectHits: 0, levelsCompleted: 0 },
            diuretics: { correctHits: 0, incorrectHits: 0, levelsCompleted: 0 },
            tubulopathies: { correctHits: 0, incorrectHits: 0, levelsCompleted: 0 },
            genetics: { correctHits: 0, incorrectHits: 0, levelsCompleted: 0 },
        };
        for (const levelData of Object.values(levelStats)) {
            score += levelData.score;
            if (levelData.facet && facets[levelData.facet]) {
                facets[levelData.facet].correctHits += levelData.correctHits;
                facets[levelData.facet].incorrectHits += levelData.incorrectHits;
                if (levelData.completed) facets[levelData.facet].levelsCompleted += 1;
            }
        }
        return { totalScore: score, facetStats: facets };
    }, [levelStats]);

    const totalLevelsByFacet = useMemo(() => {
        return Object.values(levels).reduce((acc, level) => {
            if (level.facet && level.facet !== 'onboarding') acc[level.facet] = (acc[level.facet] || 0) + 1;
            return acc;
        }, {});
    }, []);

    useEffect(() => {
        if (!showGame || !dotElRef.current || !tubRef.current) return;
        const path = tubRef.current;
        const L = path.getTotalLength();

        // Each segment's position along the path, and an equal hit-band around it. Because path-length
        // only increases as the dot travels, each band is entered once and left once — one clean, equal
        // window per segment even where the tubule coils (no PCT flicker, no DCT lingering).
        const nearestLen = (x, y) => {
            let best = 0, bd = Infinity;
            for (let s = 0; s <= L; s += 4) { const p = path.getPointAtLength(s); const d = (p.x - x) ** 2 + (p.y - y) ** 2; if (d < bd) { bd = d; best = s; } }
            for (let s = Math.max(0, best - 4); s <= Math.min(L, best + 4); s += 0.5) { const p = path.getPointAtLength(s); const d = (p.x - x) ** 2 + (p.y - y) ** 2; if (d < bd) { bd = d; best = s; } }
            return best;
        };
        const lens = {};
        segOrder.forEach(k => { lens[k] = nearestLen(segPos[k].x, segPos[k].y); });
        anchorLenRef.current = lens;
        const sorted = segOrder.map(k => lens[k]).sort((a, b) => a - b);
        let minGap = Infinity;
        for (let i = 1; i < sorted.length; i++) minGap = Math.min(minGap, sorted[i] - sorted[i - 1]);
        const W = Math.min(40, minGap * 0.45);
        wRef.current = W;

        let hold = 0;
        let animationFrameId;
        const moveDot = () => {
            if (hold > 0) {
                hold--;                                  // brief pause in the glomerulus on respawn
            } else {
                distRef.current += 2 * speed.multiplier;
                if (distRef.current >= L) { distRef.current = 0; hold = 22; }
            }
            const p = path.getPointAtLength(distRef.current);
            dotPositionRef.current = { x: p.x, y: p.y };
            if (dotElRef.current) {
                dotElRef.current.setAttribute('cx', p.x);
                dotElRef.current.setAttribute('cy', p.y);
            }
            // active segment = the band this path-position falls in
            let near = '', bd = W;
            for (const k of segOrder) { const dd = Math.abs(distRef.current - lens[k]); if (dd < bd) { bd = dd; near = k; } }
            if (near !== activeRef.current) { activeRef.current = near; setActiveSegment(near); }
            animationFrameId = requestAnimationFrame(moveDot);
        };
        animationFrameId = requestAnimationFrame(moveDot);
        return () => cancelAnimationFrame(animationFrameId);
    }, [showGame, speed.multiplier]);

    // No longer needed — data is written incrementally after each level

    const resetGameState = () => {
        setShowGame(false);
        setLevel(1);
        setSeqIndex(0);
        setLevelStats({});
        distRef.current = 0;
        activeRef.current = '';
        resetLevelState();
    };

    const handleClosePerfModal = () => {
        setShowPerfModal(false);
        if (isGameOver) {
            resetGameState();
            setIsGameOver(false);
        }
    };

    const resetLevelState = () => {
        setDisabledButtons([]);
        setHitSegments([]);
        setFeedbackText('');
        // Dot position is driven continuously by the animation loop (distRef), so it is not reset
        // per level — it simply keeps circulating the tubule.
    };

    const goToNextLevel = useCallback((completedLevelFacet) => {
        setMessage('');
        if (completedLevelFacet) {
            setLevelStats(stats => {
                const currentLevelStats = stats[level] || { score: 0, correctHits: 0, incorrectHits: 0, facet: completedLevelFacet };
                // A level left with zero interaction is a skip, not a completion.
                const interacted = ((currentLevelStats.correctHits || 0) + (currentLevelStats.incorrectHits || 0)) > 0;
                const updatedLevelStats = { ...currentLevelStats, completed: interacted };
                // Write this level's data to Firestore immediately, tagged with session, mode and presented position.
                const currentTotalScore = Object.values({ ...stats, [level]: updatedLevelStats })
                    .reduce((sum, l) => sum + (l.score || 0), 0);
                submitLevelData(userId, level, updatedLevelStats, currentTotalScore, userRole,
                    { sessionId, mode, position: seqIndex + 1 });
                return { ...stats, [level]: updatedLevelStats };
            });
        }
        const nextIndex = seqIndex + 1;
        if (nextIndex >= levelSequence.length) {
            setIsGameOver(true);
            setShowPerfModal(true);
        } else {
            setSeqIndex(nextIndex);
            setLevel(levelSequence[nextIndex]);
        }
        resetLevelState();
    }, [level, userId, userRole, sessionId, mode, seqIndex, levelSequence]);

    const goToPreviousLevel = useCallback(() => {
        if (seqIndex <= 0) return;
        const prevIndex = seqIndex - 1;
        const prevLevel = levelSequence[prevIndex];
        setMessage('');
        setLevelStats(currentStats => {
            const newStats = { ...currentStats };
            delete newStats[prevLevel];   // clear so the revisited level is replayed fresh
            return newStats;
        });
        setSeqIndex(prevIndex);
        setLevel(prevLevel);
        resetLevelState();
    }, [seqIndex, levelSequence]);

    const addEffect = (x, y, color) => {
        const id = Date.now() + Math.random();
        setEffects(currentEffects => [...currentEffects, { id, x, y, color }]);
        setTimeout(() => setEffects(cur => cur.filter(e => e.id !== id)), 650);
    };

    const handleHit = useCallback((button) => {
        const { map, facet, feedback } = levelConfig;
        const currentDotPos = dotPositionRef.current;

        // Which segment the dot is over = the path-band it's currently in (set by the animation loop).
        // Offset from the band centre is mapped onto the old 0..MAX scale so the points formula below
        // and all right/wrong logic are unchanged.
        const closestSegment = activeRef.current || null;
        const W = wRef.current || MAX_INTERACTION_DISTANCE;
        const offsetLen = closestSegment ? Math.abs(distRef.current - (anchorLenRef.current[closestSegment] ?? 0)) : Infinity;
        const minDistance = closestSegment
            ? Math.min(MAX_INTERACTION_DISTANCE - 1, (offsetLen / W) * MAX_INTERACTION_DISTANCE)
            : Infinity;

        if (closestSegment) {
            const correctTargets = map ? map[button] : null;
            let isCorrect = false;

            if (Array.isArray(correctTargets)) {
                isCorrect = correctTargets.includes(closestSegment);
            } else {
                isCorrect = correctTargets === closestSegment;
            }
            if (correctTargets === undefined) isCorrect = false;

            if (feedback) {
                if (isCorrect) {
                    const feedbackKey = Array.isArray(correctTargets) ? closestSegment : button;
                    setFeedbackText(feedback[feedbackKey] || '');
                } else {
                    const specificIncorrectFeedbackKey = `${button}_${closestSegment}`;
                    if (feedback[specificIncorrectFeedbackKey]) {
                        setFeedbackText(feedback[specificIncorrectFeedbackKey]);
                    } else if (correctTargets) {
                        if (button === 'Identify') {
                            if (level === 2) {
                                setFeedbackText(feedback[closestSegment] || "Incorrect segment.");
                            } else {
                                setFeedbackText(`Incorrect. This patient does not have a problem in this segment.`);
                            }
                        } else if (facet === 'diuretics') {
                            setFeedbackText(`Incorrect. ${button} does not act in the ${closestSegment.toUpperCase()}.`);
                        } else {
                            if ((level === 14 && button === 'Vasopressin receptor') || level === 15 || level === 16 || level === 17) {
                                setFeedbackText(`Incorrect. This patient does not have a problem in this segment.`);
                            } else {
                                setFeedbackText(`Incorrect. ${button} is not due to a problem in the ${closestSegment.toUpperCase()}.`);
                            }
                        }
                    } else {
                        setFeedbackText(feedback[button] || `That is not the correct choice for this scenario.`);
                    }
                }
            }

            const basePoints = Math.max(1, Math.round(10 * (1 - minDistance / MAX_INTERACTION_DISTANCE)));
            const points = Math.round(basePoints * speed.multiplier);
            const scoreChange = isCorrect ? points : -points;

            setLevelStats(stats => {
                const currentLevelStats = stats[level] || { score: 0, correctHits: 0, incorrectHits: 0, facet };
                return {
                    ...stats,
                    [level]: {
                        ...currentLevelStats,
                        score: currentLevelStats.score + scoreChange,
                        correctHits: currentLevelStats.correctHits + (isCorrect ? 1 : 0),
                        incorrectHits: currentLevelStats.incorrectHits + (isCorrect ? 0 : 1),
                    }
                };
            });

            if (isCorrect) {
                setMessage(`Correct! +${points}`);
                addEffect(segPos[closestSegment].x, segPos[closestSegment].y, '#4ade80');
                setTimeout(() => setMessage(''), 1500);
                if (Array.isArray(correctTargets)) {
                    setHitSegments(currentHits => {
                        if (!currentHits.includes(closestSegment)) {
                            const newHitSegments = [...currentHits, closestSegment];
                            if (newHitSegments.length === correctTargets.length) {
                                setTimeout(() => goToNextLevel(facet), 600);
                            }
                            return newHitSegments;
                        }
                        return currentHits;
                    });
                } else {
                    setDisabledButtons(currentDisabled => {
                        const newDisabled = [...currentDisabled, button];
                        if (isLevelComplete(levelConfig, newDisabled)) {
                            setTimeout(() => goToNextLevel(facet), 600);
                        }
                        return newDisabled;
                    });
                }
            } else {
                setMessage(`Incorrect! -${points}`);
                addEffect(currentDotPos.x, currentDotPos.y, '#f87171');
                setIncorrectFlash(button);
                setTimeout(() => { setMessage(''); setIncorrectFlash(''); }, 1500);
            }
        } else {
            setMessage(`Miss!`);
            addEffect(currentDotPos.x, currentDotPos.y, '#9ca3af');
            setIncorrectFlash(button);
            setTimeout(() => { setMessage(''); setIncorrectFlash(''); }, 1500);
        }
    }, [levelConfig, speed.multiplier, goToNextLevel, level]);

    // --- NEW: Begin button now opens welcome modal first ---
    const handleBeginPressed = () => {
        setShowWelcomeModal(true);
    };

    const handleWelcomeComplete = (roleData) => {
        const chosenMode = roleData?.mode || 'guided';
        const sid = newSessionId();
        const sequence = buildLevelSequence(chosenMode);
        setUserRole({ role: roleData?.role || '', practiceField: roleData?.practiceField || '' });
        setMode(chosenMode);
        setSessionId(sid);
        setLevelSequence(sequence);
        setSeqIndex(0);
        setLevel(sequence[0]);
        setLevelStats({});
        distRef.current = 0;
        activeRef.current = '';
        setShowWelcomeModal(false);
        setShowGame(true);
        // Write session doc immediately so role/mode are captured even if the player quits
        if (userId) initSessionData(userId, { ...roleData, mode: chosenMode, sessionId: sid });
    };

    if (!showGame) {
        return (
            <>
                <IntroAnimation
                    onStartGame={handleBeginPressed}
                    onShowLeaderboard={() => setShowLeaderboardModal(true)}
                    onShowInstructions={() => setShowInstructionsModal(true)}
                    onShowAdmin={() => setShowAdmin(true)}
                />
                {showWelcomeModal && <WelcomeModal onStart={handleWelcomeComplete} />}
                {showLeaderboardModal && <LeaderboardModal onClose={() => setShowLeaderboardModal(false)} />}
                {showInstructionsModal && <InstructionsModal onClose={() => setShowInstructionsModal(false)} />}
                {showAdmin && <AdminDashboard onClose={() => setShowAdmin(false)} />}
            </>
        );
    }

    return (
        <div className="bg-gray-900 text-white h-[100dvh] flex flex-col items-center justify-start font-poppins overflow-hidden p-2 sm:p-4">
            <style>{`@keyframes clickGlow { 0%,100% { box-shadow: 0 0 6px 1px rgba(96,165,250,0.55); } 50% { box-shadow: 0 0 16px 4px rgba(96,165,250,0.9); } }`}</style>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700&family=Poppins:wght@400;600&display=swap');
                .font-orbitron { font-family: 'Orbitron', sans-serif; }
                .font-poppins { font-family: 'Poppins', sans-serif; }
                @keyframes shockwave-ripple {
                    0% { transform: translate(-50%, -50%) scale(0); opacity: 0.7; }
                    100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
                }
                .shockwave {
                    position: absolute; width: 100px; height: 100px; border-radius: 50%;
                    border: 4px solid var(--shockwave-color, #f87171);
                    animation: shockwave-ripple 0.6s ease-out forwards;
                }
                @keyframes draw-path {
                    from { stroke-dashoffset: 1000; }
                    to { stroke-dashoffset: 0; }
                }
                .intro-path-draw {
                    stroke-dasharray: 1000;
                    animation: draw-path 2.5s ease-in-out forwards;
                }
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .intro-title-fade {
                    opacity: 0;
                    animation: fade-in 1s ease-out 2.5s forwards;
                }
                .intro-button-fade {
                    opacity: 0;
                    animation: fade-in 1s ease-out 3s forwards;
                }
            `}</style>

            <div className="w-full max-w-7xl mx-auto bg-gray-800 rounded-2xl shadow-lg p-2 sm:p-6 border border-gray-700 relative z-10 flex flex-col h-full">

                {showPerfModal && <PerformanceModal facetStats={facetStats} totalLevelsByFacet={totalLevelsByFacet} totalScore={totalScore} onClose={handleClosePerfModal} userId={userId} />}
                {showLeaderboardModal && <LeaderboardModal onClose={() => setShowLeaderboardModal(false)} />}
                {showInstructionsModal && <InstructionsModal onClose={() => setShowInstructionsModal(false)} />}

                {/* --- HEADER: compressed on mobile --- */}
                <div className="text-center mb-1 sm:mb-4 relative shrink-0">
                    <h1 className="text-xl sm:text-3xl font-bold text-cyan-400 font-orbitron">ENaC</h1>
                    <h2 className="text-xs sm:text-lg text-gray-400 font-orbitron -mt-0.5 sm:-mt-1">Electronic Nephron Anatomy Challenge</h2>
                    <p className="text-sm sm:text-xl font-semibold text-gray-100 mt-1">{levelConfig.title}</p>
                    <p className="text-xs sm:text-sm font-semibold text-amber-300 mt-0.5 px-8 sm:px-16">{levelConfig.task}</p>
                    <button onClick={() => setShowPerfModal(true)} className="absolute top-0 right-0 bg-gray-700/50 px-2 py-1 sm:px-3 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-transform hover:scale-110">
                        <StarIcon />
                        <span className="font-bold text-base sm:text-lg text-yellow-400">{totalScore}</span>
                    </button>
                    <button onClick={() => { setIsGameOver(true); setShowPerfModal(true); }} className="absolute top-0 left-0 bg-red-600 hover:bg-red-500 px-2 py-1 sm:px-4 sm:py-2 rounded-lg font-bold text-white text-xs sm:text-base transition-all hover:scale-110">Finish</button>
                    <InfoIcon onClick={() => setShowInstructionsModal(true)} />
                </div>

                {/* --- SPEED SELECTOR: abbreviated labels on mobile --- */}
                <div className="flex justify-center items-center gap-1.5 sm:gap-4 mb-1 sm:mb-4 shrink-0">
                    <SpeedIcon />
                    {speedOptions.map(opt => (
                        <button
                            key={opt.label}
                            onClick={() => setSpeed(opt)}
                            className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-full transition-all duration-200 ${
                                speed.label === opt.label
                                    ? 'bg-cyan-500 text-white font-bold shadow-md'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                        >
                            {/* Show first letter only on mobile, full label on sm+ */}
                            <span className="sm:hidden">{opt.label[0]}</span>
                            <span className="hidden sm:inline">{opt.label}</span>
                        </button>
                    ))}
                </div>

                {/* --- TARGETS ROW (Level 1 anatomy) --- */}
                {Array.isArray(levelConfig.map?.Identify) && !levelConfig.hideTargets && (
                    <div className="mb-1 sm:mb-4 text-center p-1.5 sm:p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg shrink-0">
                        <h4 className="text-xs sm:text-sm font-bold text-amber-400 uppercase tracking-wide mb-1">🎯 Targets</h4>
                        <div className="flex flex-wrap justify-center gap-x-1.5 gap-y-1">
                            {levelConfig.map.Identify.map(target => (
                                <span key={target} className={`px-2 py-0.5 text-xs font-mono font-bold transition-all duration-300 rounded-md border ${hitSegments.includes(target) ? 'bg-green-700/60 border-green-500 text-green-200 line-through' : 'bg-amber-800/40 border-amber-500/50 text-amber-200'}`}>
                                    {target.toUpperCase()}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {message && (
                    <div className={`absolute top-5 left-5 py-2 px-4 rounded-lg shadow-xl z-20 font-semibold ${message.includes('Correct') ? 'bg-green-500' : 'bg-red-500'} text-white`}>
                        {message}
                    </div>
                )}

                {/* --- FEEDBACK BOX: no reserved min-height --- */}
                <div className="w-full max-w-[500px] mx-auto mb-1 sm:mb-4 p-1.5 sm:p-3 bg-gray-900/50 rounded-lg flex items-center justify-center text-center shrink-0">
                    <p className="text-cyan-200 italic text-xs sm:text-sm">{feedbackText || 'Make a selection to see feedback...'}</p>
                </div>

                {/* --- MOBILE: buttons above nephron. DESKTOP (sm+): side-by-side --- */}

                {/* Action buttons — on mobile: 2-col grid above nephron. On sm+: hidden here (shown in side column) */}
                <div className={`sm:hidden grid gap-1.5 w-full shrink-0 mb-1 ${levelConfig.buttons.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    {levelConfig.buttons.map((btn) => {
                        const btnTargets = levelConfig.map?.[btn];
                        const isArray = Array.isArray(btnTargets);
                        const hitsNeeded = isArray ? btnTargets.length : 1;
                        const hitsLanded = isArray ? btnTargets.filter(t => hitSegments.includes(t)).length : 0;
                        const isPartial = isArray && hitsLanded > 0 && hitsLanded < hitsNeeded;
                        const isDone = disabledButtons.includes(btn) || (isArray && hitsLanded === hitsNeeded);
                        return (
                        <button
                            key={btn}
                            onClick={() => handleHit(btn)}
                            disabled={isDone}
                            className={`
                                px-3 py-2
                                ${levelConfig.buttons.length === 1 ? 'max-w-[55%] mx-auto w-full' : 'w-full'}
                                rounded-lg
                                flex items-center justify-center text-center
                                font-semibold text-xs whitespace-normal [overflow-wrap:anywhere] leading-tight shadow-lg border-2
                                transition-all duration-200 focus:outline-none
                                ${
                                    isDone
                                        ? 'bg-gradient-to-br from-green-500 to-green-700 border-green-400 text-white cursor-not-allowed'
                                        : isPartial
                                        ? 'bg-gradient-to-r from-green-600 to-blue-700 border-green-400 text-white shadow-green-500/40 shadow-md'
                                        : incorrectFlash === btn
                                        ? 'bg-gradient-to-br from-red-500 to-red-700 border-red-400 text-white animate-pulse'
                                        : 'bg-gradient-to-br from-blue-600 to-blue-800 border-blue-400 text-white animate-[clickGlow_1.8s_ease-in-out_infinite]'
                                }`}
                        >
                            {isDone ? `✓ ${btn}` : isPartial ? `½ ${btn} (${hitsLanded}/${hitsNeeded})` : btn}
                        </button>
                        );
                    })}
                </div>

                {/* Mobile nav buttons row */}
                <div className="sm:hidden flex items-center justify-center gap-3 shrink-0 mb-1">
                    <button onClick={goToPreviousLevel} disabled={seqIndex === 0}
                        className="px-4 py-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700/50 disabled:cursor-not-allowed rounded-full font-bold transition-colors shadow-lg text-xs">
                        ⏮ Back
                    </button>
                    <button onClick={() => goToNextLevel(levelConfig.facet)} disabled={seqIndex >= levelSequence.length - 1}
                        className="px-4 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-full font-bold transition-colors shadow-lg text-xs">
                        Skip ⏭
                    </button>
                </div>

                {/* Nephron — full width on mobile, left column on desktop */}
                <div className="flex flex-row gap-2 sm:gap-6 flex-grow min-h-0 min-w-0 w-full">

                    <div
                        ref={playfieldRef}
                        className="relative w-full sm:w-2/3 min-h-0 min-w-0 self-stretch"
                    >
                        {/* Absolute-fill SVG: reliably fills whatever space the flex row leaves. viewBox
                            fixed at 540x660; preserveAspectRatio 'meet' scales the WHOLE drawing to fit,
                            so nothing is ever clipped and nothing overflows. */}
                        <svg viewBox="0 0 540 660" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
                            <defs>
                                <radialGradient id="glom" cx="42%" cy="40%" r="60%">
                                    <stop offset="0%" stopColor="#fecaca"/><stop offset="65%" stopColor="#ef4444"/><stop offset="100%" stopColor="#991b1b"/>
                                </radialGradient>
                                <linearGradient id="cortexG" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#17233b"/><stop offset="100%" stopColor="#141d30"/>
                                </linearGradient>
                                <linearGradient id="medullaG" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#101a2e"/><stop offset="100%" stopColor="#0b1424"/>
                                </linearGradient>
                            </defs>

                            {/* Cortex / medulla */}
                            <rect x="0" y="0" width="540" height="250" fill="url(#cortexG)"/>
                            <rect x="0" y="250" width="540" height="410" fill="url(#medullaG)"/>
                            <line x1="0" y1="250" x2="540" y2="250" stroke="#334155" strokeWidth="1.2" strokeDasharray="6 6" opacity="0.7"/>
                            <text x="14" y="28" fontSize="12" fontWeight="700" letterSpacing="2" fill="#64748b">CORTEX</text>
                            <text x="14" y="278" fontSize="12" fontWeight="700" letterSpacing="2" fill="#64748b">MEDULLA</text>

                            {/* Arterioles + glomerulus (decorative; the dot respawns here) */}
                            <path d="M 18,150 C 45,150 50,132 70,126" fill="none" stroke="#b91c1c" strokeWidth="6" strokeLinecap="round" opacity="0.85"/>
                            <path d="M 96,128 C 120,120 128,140 150,140" fill="none" stroke="#7f1d1d" strokeWidth="5" strokeLinecap="round" opacity="0.8"/>
                            <circle cx="80" cy="120" r="26" fill="url(#glom)"/>
                            <path d="M 80,120 q 10,-8 3,-18 M 80,120 q -12,-4 -16,6 M 80,120 q 12,8 24,3 M 80,120 q -6,12 4,18 M 80,120 q 14,-2 10,12" stroke="#7f1d1d" strokeWidth="2.5" fill="none" opacity="0.55"/>

                            {/* Tubule wall + lumen (share the path the dot follows) */}
                            <path d={NEPHRON_D} fill="none" stroke="#a15c1b" strokeWidth="20" strokeLinecap="round" strokeLinejoin="round"/>
                            <path ref={tubRef} d={NEPHRON_D} fill="none" stroke="#fbbf24" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round"/>

                            {/* Segment markers + callout labels */}
                            {segOrder.map((key) => {
                                const pos = segPos[key];
                                const lab = SEG_LABELS[key];
                                const hit = hitSegments.includes(key);
                                const act = key === activeSegment;
                                return (
                                    <g key={key} style={{ pointerEvents: 'none' }}>
                                        <path d={`M ${pos.x},${pos.y} L ${lab.lx},${lab.ly}`} stroke="#475569" strokeWidth="1" fill="none"/>
                                        <circle
                                            cx={pos.x} cy={pos.y} r={act ? SEGMENT_RADIUS + 2 : SEGMENT_RADIUS}
                                            fill={hit ? 'rgba(34,197,94,0.55)' : act ? 'rgba(56,189,248,0.40)' : 'rgba(148,163,184,0.12)'}
                                            stroke={hit ? '#22c55e' : act ? '#38bdf8' : 'rgba(210,220,235,0.9)'}
                                            strokeWidth={hit || act ? 2 : 1.6}
                                            strokeDasharray={hit || act ? 'none' : '4 3'}
                                            style={{ transition: 'fill 0.2s' }}
                                        />
                                        <text x={lab.lx} y={lab.ly} textAnchor={lab.anchor} fontSize="11.5" fontWeight="600" fill="#dbe4ef">
                                            {lab.lines.length > 1
                                                ? lab.lines.map((ln, i) => <tspan key={i} x={lab.lx} dy={i === 0 ? 0 : 13}>{ln}</tspan>)
                                                : lab.lines[0]}
                                        </text>
                                    </g>
                                );
                            })}

                            {/* Shockwave effects (rendered in SVG coords so they always align) */}
                            {effects.map(effect => (
                                <circle key={effect.id} cx={effect.x} cy={effect.y} r="6" fill="none" stroke={effect.color} strokeWidth="3">
                                    <animate attributeName="r" from="6" to="42" dur="0.6s" fill="freeze"/>
                                    <animate attributeName="opacity" from="0.9" to="0" dur="0.6s" fill="freeze"/>
                                </circle>
                            ))}

                            {/* Travelling dot */}
                            <circle
                                ref={dotElRef}
                                cx={START_POS.x}
                                cy={START_POS.y}
                                r={DOT_RADIUS}
                                fill="rgba(56,189,248,0.95)"
                                stroke="white"
                                strokeWidth="2"
                                style={{
                                    filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.9))',
                                    animation: 'pulse 2s infinite',
                                }}
                            />
                        </svg>
                    </div>

                    {/* Desktop-only right column: buttons + nav */}
                    <div className="hidden sm:flex flex-col items-center w-1/3 min-h-0">
                        <div className="flex flex-row flex-wrap justify-center gap-3 w-full overflow-y-auto flex-grow">
                            {levelConfig.buttons.map((btn) => {
                                const btnTargets = levelConfig.map?.[btn];
                                const isArray = Array.isArray(btnTargets);
                                const hitsNeeded = isArray ? btnTargets.length : 1;
                                const hitsLanded = isArray ? btnTargets.filter(t => hitSegments.includes(t)).length : 0;
                                const isPartial = isArray && hitsLanded > 0 && hitsLanded < hitsNeeded;
                                const isDone = disabledButtons.includes(btn) || (isArray && hitsLanded === hitsNeeded);
                                return (
                                <button
                                    key={btn}
                                    onClick={() => handleHit(btn)}
                                    disabled={isDone}
                                    className={`
                                        w-24 h-24
                                        px-1 py-1
                                        rounded-full
                                        flex items-center justify-center text-center
                                        font-semibold text-xs whitespace-normal [overflow-wrap:anywhere] leading-tight shadow-lg border-2
                                        transition-all duration-200 transform hover:-translate-y-1
                                        focus:outline-none flex-shrink-0
                                        ${
                                            isDone
                                                ? 'bg-gradient-to-br from-green-500 to-green-700 border-green-400 text-white cursor-not-allowed'
                                                : isPartial
                                                ? 'bg-gradient-to-r from-green-600 to-blue-700 border-green-400 text-white shadow-green-500/40 shadow-md'
                                                : incorrectFlash === btn
                                                ? 'bg-gradient-to-br from-red-500 to-red-700 border-red-400 text-white animate-pulse'
                                                : 'bg-gradient-to-br from-blue-600 to-blue-800 border-blue-400 text-white animate-[clickGlow_1.8s_ease-in-out_infinite] hover:shadow-blue-400/60 hover:shadow-lg'
                                        }`}
                                >
                                    {isDone ? `✓ ${btn}` : isPartial ? `½ ${btn} (${hitsLanded}/${hitsNeeded})` : btn}
                                </button>
                                );
                            })}
                        </div>
                        <div className="flex items-center gap-2 mt-6 shrink-0">
                            <button onClick={goToPreviousLevel} disabled={seqIndex === 0}
                                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700/50 disabled:cursor-not-allowed rounded-full font-bold transition-colors shadow-lg">
                                ⏮ Go Back
                            </button>
                            <button onClick={() => goToNextLevel(levelConfig.facet)} disabled={seqIndex >= levelSequence.length - 1}
                                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-full font-bold transition-colors shadow-lg">
                                Skip ⏭
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}